/**
 * Cold Outreach Page
 * Manage cold email campaigns with multi-step sequences,
 * prospect management, activity tracking, and reply handling.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import PhoneInput from '@/components/ui/PhoneInput';
import {
  Mail,
  Plus,
  Search,
  Filter,
  Play,
  Pause,
  RefreshCw,
  MoreVertical,
  ExternalLink,
  Clock,
  TrendingUp,
  Target,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  Eye,
  MousePointer,
  MessageSquare,
  Calendar,
  Edit,
  Trash2,
  Copy,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Loader2,
  BarChart3,
  Send,
  Inbox,
  Settings,
  FileText,
  UserPlus,
  Upload,
  ArrowRight,
  Zap,
  Server,
  Shield,
  Globe,
  Key,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  coldOutreachApi,
  ColdOutreachCampaign,
  SequenceStep,
  ColdOutreachProspect,
  ColdOutreachActivity,
  ColdOutreachReply,
  CampaignAnalytics,
  CampaignStatus,
  CampaignType,
  StepType,
  ProspectStatus,
  ReplyClassification,
  EmailTemplate,
  SendersResponse,
} from '../../shared/services/platformApi';
import { salesApi, leadBuilderApi } from '../../shared/services/platformApi';
import { useTwilioVoice } from '@/hooks/useTwilioVoice';
import CampaignTemplates from '@/components/admin/CampaignTemplates';
import {
  BriefStatusChip,
  CampaignPersonalizationSettings,
  ProspectBriefDialog,
  formatUsd,
} from '../components/cold-outreach/Personalization';
import { tvTokens, TvPageBar, TvButton } from '../../shared/components/triveny';
import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

type PageTab = 'campaigns' | 'templates' | 'settings' | 'domains' | 'calls';
type CampaignDetailTab = 'overview' | 'sequence' | 'prospects' | 'activity' | 'replies' | 'analytics';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function getStatusColor(status: string): string {
  switch (status) {
    case 'draft': return 'bg-gray-100 text-gray-800';
    case 'scheduled': return 'bg-blue-100 text-blue-800';
    case 'active': return 'bg-green-100 text-green-800';
    case 'paused': return 'bg-yellow-100 text-yellow-800';
    case 'completed': return 'bg-purple-100 text-purple-800';
    case 'cancelled': return 'bg-red-100 text-red-800';
    case 'pending': return 'bg-gray-100 text-gray-800';
    case 'replied': return 'bg-green-100 text-green-800';
    case 'bounced': return 'bg-red-100 text-red-800';
    case 'opted_out': return 'bg-orange-100 text-orange-800';
    default: return 'bg-gray-100 text-gray-600';
  }
}

function getReplyColor(classification: string): string {
  switch (classification) {
    case 'positive': return 'bg-green-100 text-green-800';
    case 'negative': return 'bg-red-100 text-red-800';
    case 'neutral': return 'bg-gray-100 text-gray-800';
    case 'meeting_request': return 'bg-purple-100 text-purple-800';
    case 'out_of_office': return 'bg-yellow-100 text-yellow-800';
    case 'bounce': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-600';
  }
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatPercentage(value: number, total: number): string {
  if (total === 0) return '0%';
  return `${Math.round((value / total) * 100)}%`;
}

// ============================================================================
// CREATE CAMPAIGN MODAL
// ============================================================================

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (campaign: Record<string, any>) => void;
}

const DAY_TO_INT: Record<string, number> = {
  monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 7,
};

const EMPTY_CAMPAIGN_FORM = {
  name: '',
  description: '',
  campaign_type: 'cold_email' as CampaignType,
  from_email: '',
  from_name: '',
  reply_to_email: '',
  daily_send_limit: 50,
  timezone: 'America/New_York',
  sending_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  sending_start_time: '09:00',
  sending_end_time: '17:00',
  track_opens: true,
  track_clicks: true,
  stop_on_reply: true,
  stop_on_meeting: true,
};

function CreateCampaignModal({ isOpen, onClose, onSubmit }: CreateCampaignModalProps) {
  const [formData, setFormData] = useState({ ...EMPTY_CAMPAIGN_FORM });

  // Audience / template / sender pickers
  const [outreachLists, setOutreachLists] = useState<any[]>([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [senders, setSenders] = useState<SendersResponse | null>(null);
  const [senderMode, setSenderMode] = useState<'mailbox' | 'custom'>('mailbox');
  const [newMailboxUser, setNewMailboxUser] = useState('');
  const [newMailboxDomainId, setNewMailboxDomainId] = useState('');
  const [creatingMailbox, setCreatingMailbox] = useState(false);

  const loadSenders = useCallback(async () => {
    try {
      const resp = await coldOutreachApi.getSenders();
      if (resp.success && resp.data) {
        setSenders(resp.data);
        return resp.data;
      }
    } catch (err) {
      console.error('Failed to load senders:', err);
    }
    return null;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      const data = await loadSenders();
      // Pre-populate sender from the recommended warmup-domain mailbox
      if (data?.recommended_sender) {
        setSenderMode('mailbox');
        setFormData(prev => ({
          ...prev,
          from_email: prev.from_email || data.recommended_sender!.email_address,
          from_name: prev.from_name || data.recommended_sender!.display_name || '',
        }));
      } else {
        setSenderMode(data && data.domains.length > 0 ? 'mailbox' : 'custom');
      }
    })();
    leadBuilderApi.getOutreachLists().then((resp) => {
      if (resp.success && resp.data) setOutreachLists(resp.data);
    }).catch((err) => console.error('Failed to load outreach lists:', err));
    coldOutreachApi.getTemplates().then((resp) => {
      if (resp.success && resp.data) setTemplates(resp.data.templates || []);
    }).catch((err) => console.error('Failed to load templates:', err));
  }, [isOpen, loadSenders]);

  const handleCreateMailbox = async (domain: { domain_id: string; domain: string }) => {
    const username = newMailboxUser.trim() || 'outreach';
    setCreatingMailbox(true);
    try {
      const resp = await coldOutreachApi.createMailbox(domain.domain_id, {
        email_address: `${username}@${domain.domain}`,
        display_name: formData.from_name || username,
      });
      if (resp.success) {
        const data = await loadSenders();
        const created = `${username}@${domain.domain}`;
        setFormData(prev => ({ ...prev, from_email: created }));
        setNewMailboxDomainId('');
        setNewMailboxUser('');
        if (!data) console.warn('Sender list refresh failed after mailbox creation');
      } else {
        alert(`Could not create mailbox: ${resp.error || 'unknown error'}`);
      }
    } catch (err) {
      console.error('Create mailbox failed:', err);
      alert('Could not create mailbox');
    } finally {
      setCreatingMailbox(false);
    }
  };

  const selectedList = outreachLists.find(l => l.id === selectedListId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      description: formData.description,
      campaign_type: formData.campaign_type,
      // Backend field names (the old form posted from_email/from_name, which
      // the API silently dropped — the campaign never had a sender)
      sending_email: formData.from_email,
      sending_name: formData.from_name,
      reply_to_email: formData.reply_to_email || undefined,
      daily_send_limit: formData.daily_send_limit,
      timezone: formData.timezone,
      send_days: formData.sending_days.map(d => DAY_TO_INT[d]).filter(Boolean).sort(),
      send_window_start: formData.sending_start_time,
      send_window_end: formData.sending_end_time,
      // Audience + first-step template
      source_list_id: selectedListId || undefined,
      source_list_type: selectedList ? (selectedList.list_source === 'contact_list' ? 'contact_list' : 'lead_builder') : undefined,
      template_id: selectedTemplateId || undefined,
    });
    setFormData({ ...EMPTY_CAMPAIGN_FORM });
    setSelectedListId('');
    setSelectedTemplateId('');
    onClose();
  };

  const toggleDay = (day: string) => {
    const days = formData.sending_days;
    if (days.includes(day)) {
      setFormData({ ...formData, sending_days: days.filter(d => d !== day) });
    } else {
      setFormData({ ...formData, sending_days: [...days, day] });
    }
  };

  const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Create Cold Outreach Campaign
          </DialogTitle>
          <DialogDescription>
            Set up a new cold email sequence to reach prospects.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Campaign Details</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Q1 Decision Makers Outreach"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Brief description of the campaign"
              />
            </div>
          </div>

          {/* Audience */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Audience</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Outreach List</label>
              <select
                value={selectedListId}
                onChange={(e) => setSelectedListId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">No list — add prospects later</option>
                {outreachLists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.contact_count} contact{l.contact_count === 1 ? '' : 's'}
                    {l.list_source === 'contact_list' ? ', contact list' : ''})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Contacts with an email are enrolled as prospects when the campaign is created.
              </p>
            </div>
          </div>

          {/* First email template */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">First Email</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template for Step 1</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">No template — draft the sequence afterwards</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.template_name}{t.is_custom ? ' (custom)' : ''}{t.subject_line ? ` — ${t.subject_line}` : ''}
                  </option>
                ))}
              </select>
              {selectedTemplateId && (
                <p className="text-xs text-gray-500 mt-1">
                  Step 1 of the sequence is created from this template; edit it any time in the Sequence tab.
                </p>
              )}
            </div>
          </div>

          {/* Sender Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Sender</h3>
            {senders && senders.domains.length > 0 && (
              <div className="flex gap-3 text-sm">
                <label className="flex items-center gap-1.5">
                  <input type="radio" checked={senderMode === 'mailbox'} onChange={() => setSenderMode('mailbox')} />
                  Use an outreach domain mailbox
                </label>
                <label className="flex items-center gap-1.5">
                  <input type="radio" checked={senderMode === 'custom'} onChange={() => setSenderMode('custom')} />
                  Custom address
                </label>
              </div>
            )}
            {senderMode === 'mailbox' && senders && senders.domains.length > 0 ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Email *</label>
                  <select
                    required
                    value={formData.from_email}
                    onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">Select a mailbox…</option>
                    {senders.domains.filter(d => d.mailboxes.length > 0).map((d) => (
                      <optgroup key={d.domain_id} label={`${d.domain} (${d.warmup_status.replace('_', ' ')})`}>
                        {d.mailboxes.map((mb) => (
                          <option key={mb.mailbox_id} value={mb.email_address}>{mb.email_address}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {senders.recommended_sender && formData.from_email === senders.recommended_sender.email_address && (
                    <p className="text-xs text-green-600 mt-1">
                      Recommended: this mailbox is on your {senders.recommended_sender.warmup_status} domain {senders.recommended_sender.domain}.
                    </p>
                  )}
                </div>
                {senders.domains.filter(d => d.needs_mailbox).map((d) => (
                  <div key={d.domain_id} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    <p className="mb-2">
                      <strong>{d.domain}</strong> ({d.warmup_status.replace('_', ' ')}) has no sender mailbox yet — set one up to send from it.
                    </p>
                    {newMailboxDomainId === d.domain_id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newMailboxUser}
                          onChange={(e) => setNewMailboxUser(e.target.value.replace(/[^a-zA-Z0-9._-]/g, ''))}
                          className="px-2 py-1 border rounded w-28"
                          placeholder="outreach"
                        />
                        <span className="text-amber-700">@{d.domain}</span>
                        <button
                          type="button"
                          disabled={creatingMailbox}
                          onClick={() => handleCreateMailbox(d)}
                          className="px-2 py-1 bg-amber-600 text-white rounded disabled:opacity-50"
                        >
                          {creatingMailbox ? 'Creating…' : 'Create'}
                        </button>
                        <button type="button" onClick={() => setNewMailboxDomainId('')} className="text-amber-700">Cancel</button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setNewMailboxDomainId(d.domain_id)}
                        className="px-2 py-1 border border-amber-300 rounded hover:bg-amber-100 font-medium"
                      >
                        Set up sender email on {d.domain}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Email *</label>
                <input
                  type="email"
                  required
                  value={formData.from_email}
                  onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="john@company.com"
                />
                {senders && senders.domains.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No outreach domains yet — add and warm up a domain in the Domains tab for better deliverability.
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Name *</label>
                <input
                  type="text"
                  required
                  value={formData.from_name}
                  onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Sender name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reply-To Email</label>
                <input
                  type="email"
                  value={formData.reply_to_email}
                  onChange={(e) => setFormData({ ...formData, reply_to_email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Leave blank to use From Email"
                />
              </div>
            </div>
          </div>

          {/* Sending Schedule */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Sending Schedule</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sending Days</label>
              <div className="flex flex-wrap gap-2">
                {allDays.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                      formData.sending_days.includes(day)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input
                  type="time"
                  value={formData.sending_start_time}
                  onChange={(e) => setFormData({ ...formData, sending_start_time: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <input
                  type="time"
                  value={formData.sending_end_time}
                  onChange={(e) => setFormData({ ...formData, sending_end_time: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Daily Limit</label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={formData.daily_send_limit}
                  onChange={(e) => setFormData({ ...formData, daily_send_limit: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Tracking Options */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Tracking & Behavior</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.track_opens}
                  onChange={(e) => setFormData({ ...formData, track_opens: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Track email opens</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.track_clicks}
                  onChange={(e) => setFormData({ ...formData, track_clicks: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Track link clicks</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.stop_on_reply}
                  onChange={(e) => setFormData({ ...formData, stop_on_reply: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Stop sequence when prospect replies</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.stop_on_meeting}
                  onChange={(e) => setFormData({ ...formData, stop_on_meeting: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Stop sequence when meeting is booked</span>
              </label>
            </div>
          </div>

          <DialogFooter className="pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Campaign
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ADD STEP MODAL
// ============================================================================

interface AddStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (step: Partial<SequenceStep>) => void;
  stepNumber: number;
}

function AddStepModal({ isOpen, onClose, onSubmit, stepNumber }: AddStepModalProps) {
  const [formData, setFormData] = useState({
    step_type: 'email' as StepType,
    name: '',
    delay_days: stepNumber === 1 ? 0 : 3,
    delay_hours: 0,
    subject_template: '',
    body_template: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      step_number: stepNumber,
    });
    setFormData({
      step_type: 'email',
      name: '',
      delay_days: 3,
      delay_hours: 0,
      subject_template: '',
      body_template: '',
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Step {stepNumber}</DialogTitle>
          <DialogDescription>
            Configure the email content and timing for this step.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Step Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={`Step ${stepNumber}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Step Type</label>
              <select
                value={formData.step_type}
                onChange={(e) => setFormData({ ...formData, step_type: e.target.value as StepType })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="email">Email</option>
                <option value="linkedin_connect">LinkedIn Connection Request</option>
                <option value="linkedin_message">LinkedIn Message</option>
                <option value="call">Call Task</option>
              </select>
            </div>
          </div>

          {stepNumber > 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wait Days</label>
                <input
                  type="number"
                  min="0"
                  value={formData.delay_days}
                  onChange={(e) => setFormData({ ...formData, delay_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wait Hours</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={formData.delay_hours}
                  onChange={(e) => setFormData({ ...formData, delay_hours: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
          )}

          {formData.step_type === 'email' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line *</label>
                <input
                  type="text"
                  required
                  value={formData.subject_template}
                  onChange={(e) => setFormData({ ...formData, subject_template: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Use {{first_name}}, {{company_name}} for personalization"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Body *</label>
                <textarea
                  required
                  value={formData.body_template}
                  onChange={(e) => setFormData({ ...formData, body_template: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                  rows={10}
                  placeholder={`Hi {{first_name}},

I noticed {{company_name}} is doing great work in...

Available variables: {{first_name}}, {{last_name}}, {{company_name}}, {{job_title}}`}
                />
              </div>
            </>
          )}

          <div className="bg-blue-50 p-3 rounded-lg text-sm">
            <strong>Personalization Variables:</strong>
            <div className="mt-1 text-gray-600">
              {'{'}{'{'} first_name {'}'}{'}'}, {'{'}{'{'} last_name {'}'}{'}'}, {'{'}{'{'} company_name {'}'}{'}'}, {'{'}{'{'} job_title {'}'}{'}'}, {'{'}{'{'} email {'}'}{'}'}
            </div>
          </div>

          <DialogFooter>
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Add Step
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ADD PROSPECT MODAL
// ============================================================================

interface AddProspectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (prospect: Partial<ColdOutreachProspect>) => void;
}

function AddProspectModal({ isOpen, onClose, onSubmit }: AddProspectModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    job_title: '',
    linkedin_url: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      company_name: '',
      job_title: '',
      linkedin_url: '',
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Prospect</DialogTitle>
          <DialogDescription>
            Add a new prospect to this campaign.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
            <input
              type="text"
              value={formData.job_title}
              onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <DialogFooter>
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Add Prospect
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// SMTP CONFIGURATION MODAL
// ============================================================================

interface SmtpConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: SmtpConfig) => void;
}

interface SmtpConfig {
  host: string;
  port: number;
  username: string;
  password: string;
  encryption: 'tls' | 'ssl' | 'none';
  from_email: string;
  from_name: string;
}

function SmtpConfigModal({ isOpen, onClose, onSave }: SmtpConfigModalProps) {
  const [formData, setFormData] = useState<SmtpConfig>({
    host: '',
    port: 587,
    username: '',
    password: '',
    encryption: 'tls',
    from_email: '',
    from_name: '',
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    // Simulate test connection
    await new Promise(resolve => setTimeout(resolve, 1500));
    if (formData.host && formData.username && formData.password) {
      setTestResult({ success: true, message: 'Connection successful! SMTP settings are valid.' });
    } else {
      setTestResult({ success: false, message: 'Please fill in all required fields.' });
    }
    setTesting(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            SMTP Configuration
          </DialogTitle>
          <DialogDescription>
            Configure your SMTP server settings for sending cold outreach emails.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Server Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Server Settings</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host *</label>
                <input
                  type="text"
                  required
                  value={formData.host}
                  onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Port *</label>
                <input
                  type="number"
                  required
                  value={formData.port}
                  onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="587"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Encryption</label>
              <select
                value={formData.encryption}
                onChange={(e) => setFormData({ ...formData, encryption: e.target.value as 'tls' | 'ssl' | 'none' })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="tls">TLS (Recommended)</option>
                <option value="ssl">SSL</option>
                <option value="none">None</option>
              </select>
            </div>
          </div>

          {/* Authentication */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Authentication</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
              <input
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="your-email@domain.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password / App Password *</label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••••••"
              />
              <p className="text-xs text-gray-500 mt-1">
                For Gmail, use an App Password. For other providers, use your SMTP password.
              </p>
            </div>
          </div>

          {/* Sender Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Default Sender</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
                <input
                  type="email"
                  value={formData.from_email}
                  onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="outreach@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Name</label>
                <input
                  type="text"
                  value={formData.from_name}
                  onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Sales Team"
                />
              </div>
            </div>
          </div>

          {/* Test Connection */}
          {testResult && (
            <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              <div className="flex items-center gap-2">
                {testResult.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                {testResult.message}
              </div>
            </div>
          )}

          <DialogFooter className="pt-4">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testing}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Test Connection
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Configuration
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// DOMAIN VERIFICATION MODAL
// ============================================================================

interface DomainVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DomainRecord {
  type: string;
  name: string;
  value: string;
  verified: boolean;
}

function DomainVerificationModal({ isOpen, onClose }: DomainVerificationModalProps) {
  const [domain, setDomain] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [records, setRecords] = useState<DomainRecord[]>([]);
  const [showRecords, setShowRecords] = useState(false);

  const handleVerifyDomain = async () => {
    if (!domain) return;

    setVerifying(true);
    // Simulate DNS lookup
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Generate DNS records that need to be added
    const generatedRecords: DomainRecord[] = [
      {
        type: 'TXT',
        name: `_dmarc.${domain}`,
        value: 'v=DMARC1; p=none; rua=mailto:dmarc@weavein.ai',
        verified: false,
      },
      {
        type: 'TXT',
        name: domain,
        value: `v=spf1 include:weavein.ai ~all`,
        verified: false,
      },
      {
        type: 'CNAME',
        name: `wv._domainkey.${domain}`,
        value: 'wv.domainkey.weavein.ai',
        verified: false,
      },
    ];

    setRecords(generatedRecords);
    setShowRecords(true);
    setVerifying(false);
  };

  const handleCheckVerification = async () => {
    setVerifying(true);
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Simulate checking DNS records - randomly verify some
    setRecords(prev => prev.map(record => ({
      ...record,
      verified: Math.random() > 0.5,
    })));

    setVerifying(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Domain Verification
          </DialogTitle>
          <DialogDescription>
            Verify your sending domain to improve email deliverability and avoid spam filters.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Domain Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Sending Domain</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => {
                    setDomain(e.target.value);
                    setShowRecords(false);
                  }}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="company.com"
                />
              </div>
              <button
                onClick={handleVerifyDomain}
                disabled={!domain || verifying}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                Generate Records
              </button>
            </div>
          </div>

          {/* DNS Records */}
          {showRecords && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Add these DNS records to your domain</h4>
                <p className="text-sm text-blue-700">
                  Log in to your domain registrar and add the following DNS records.
                  It may take up to 48 hours for DNS changes to propagate.
                </p>
              </div>

              <div className="space-y-3">
                {records.map((record, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 text-xs font-mono bg-gray-100 rounded">{record.type}</span>
                        {record.verified ? (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle className="h-4 w-4" /> Verified
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-yellow-600 text-sm">
                            <AlertCircle className="h-4 w-4" /> Pending
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => copyToClipboard(record.value)}
                        className="text-gray-500 hover:text-gray-700"
                        title="Copy value"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="text-gray-500">Name: </span>
                        <span className="font-mono text-gray-900">{record.name}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Value: </span>
                        <span className="font-mono text-gray-900 break-all">{record.value}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleCheckVerification}
                disabled={verifying}
                className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Checking DNS Records...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Check Verification Status
                  </>
                )}
              </button>

              {records.every(r => r.verified) && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Domain fully verified!</span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    Your domain is now verified and ready for sending cold outreach emails.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// DOMAIN POOL TAB
// ============================================================================

function DomainPoolTab() {
  const [domains, setDomains] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [addingSuggestion, setAddingSuggestion] = useState<string | null>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityResults, setAvailabilityResults] = useState<Record<string, { available: boolean | null; price?: number | null; error?: string }>>({});
  const [newDomain, setNewDomain] = useState('');
  const [newPrimaryDomain, setNewPrimaryDomain] = useState('');
  const [seedEmails, setSeedEmails] = useState<string[]>([]);
  const [newSeedEmail, setNewSeedEmail] = useState('');
  const [seedLoading, setSeedLoading] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<any>(null);
  const [healthOverview, setHealthOverview] = useState<any>(null);
  const [dnsRecords, setDnsRecords] = useState<any[]>([]);
  const [warmupLog, setWarmupLog] = useState<any[]>([]);
  const [warmupStatus, setWarmupStatus] = useState<any>(null);
  const [warmupSends, setWarmupSends] = useState<any[]>([]);
  const [domainMailboxes, setDomainMailboxes] = useState<any[]>([]);
  const [newMailboxEmail, setNewMailboxEmail] = useState('');
  const [newMailboxName, setNewMailboxName] = useState('');
  const [copiedRecord, setCopiedRecord] = useState<string | null>(null);
  const [procRegistrar, setProcRegistrar] = useState<'godaddy' | 'namecheap' | 'cloudflare'>('godaddy');
  const [purchasing, setPurchasing] = useState<string | null>(null);

  const fetchDomains = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await coldOutreachApi.getDomains();
      if (resp.data) setDomains(resp.data.domains || []);
    } catch (err) { console.error('Failed to fetch domains:', err); }
    finally { setLoading(false); }
  }, []);

  const fetchHealthOverview = useCallback(async () => {
    try {
      const resp = await coldOutreachApi.getDomainHealthOverview();
      if (resp.data) setHealthOverview(resp.data);
    } catch (err) { console.error('Failed to fetch health overview:', err); }
  }, []);

  useEffect(() => {
    fetchDomains(); fetchHealthOverview();
    // Load org-level seed config
    coldOutreachApi.getWarmupSeedConfig().then(resp => {
      if (resp.data?.seed_emails) setSeedEmails(resp.data.seed_emails);
    }).catch(() => {});
  }, [fetchDomains, fetchHealthOverview]);

  // Fetch detail data when a domain is selected
  useEffect(() => {
    if (!selectedDomain) return;
    const fetchDetails = async () => {
      try {
        const [dnsResp, warmupResp, mailboxResp, seedResp, statusResp, sendsResp] = await Promise.all([
          coldOutreachApi.getDnsRecords(selectedDomain.id),
          coldOutreachApi.getWarmupLog(selectedDomain.id, 30),
          coldOutreachApi.getMailboxes(selectedDomain.id),
          coldOutreachApi.getWarmupSeedConfig(),
          coldOutreachApi.getWarmupStatus(selectedDomain.id).catch(() => null),
          coldOutreachApi.getWarmupSends(selectedDomain.id, 25).catch(() => null),
        ]);
        if (dnsResp.data) {
          let records = dnsResp.data.dns_records || [];
          if (typeof records === 'string') {
            try { records = JSON.parse(records); } catch { records = []; }
          }
          if (!Array.isArray(records)) records = [];
          setDnsRecords(records);
        }
        if (warmupResp.data) setWarmupLog(warmupResp.data.warmup_log || []);
        if (mailboxResp.data) setDomainMailboxes(mailboxResp.data.mailboxes || []);
        if (seedResp.data) setSeedEmails(seedResp.data.seed_emails || []);
        if (statusResp?.data) setWarmupStatus(statusResp.data);
        if (sendsResp?.data) setWarmupSends(sendsResp.data.sends || []);
      } catch (err) { console.error('Failed to fetch domain details:', err); }
    };
    fetchDetails();
  }, [selectedDomain]);

  const handleGenerateSuggestions = async () => {
    if (!primaryDomain) return;
    try {
      const resp = await coldOutreachApi.generateDomainSuggestions(primaryDomain);
      if (resp.data) setSuggestions(resp.data.suggestions || []);
      setShowSuggestions(true);
    } catch (err) { console.error('Failed to generate suggestions:', err); }
  };

  const handleCheckAvailability = async () => {
    if (suggestions.length === 0) return;
    setCheckingAvailability(true);
    try {
      const domainList = suggestions.map(s => s.domain);
      // Check via the selected registrar (requires registrar API keys in Settings)
      const resp = await coldOutreachApi.checkDomainAvailability(domainList, procRegistrar);
      if (resp.data?.results) {
        const results: Record<string, any> = {};
        resp.data.results.forEach((r: any) => {
          results[r.domain] = { available: r.available, price: r.price, error: r.error };
        });
        setAvailabilityResults(results);
      }
    } catch (err) {
      // If registrar not configured, mark all as "unchecked"
      console.warn('Availability check failed (registrar may not be configured):', err);
      const results: Record<string, any> = {};
      suggestions.forEach(s => {
        results[s.domain] = { available: null, error: 'Registrar not configured' };
      });
      setAvailabilityResults(results);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handlePurchaseDomain = async (domain: string) => {
    if (!confirm(`Purchase ${domain} via ${procRegistrar}? This registers the domain (a paid, 1-year registration) and adds it to your pool.`)) return;
    setPurchasing(domain);
    try {
      const resp = await coldOutreachApi.purchaseDomain({ domain, primary_domain: primaryDomain, registrar: procRegistrar, years: 1 });
      const r: any = resp?.data || {};
      alert(r.error ? `Purchase failed: ${r.error}` : `✅ Purchased ${domain} via ${procRegistrar} and added to your pool.`);
      fetchDomains();
      fetchHealthOverview();
      setSuggestions(prev => prev.filter(s => s.domain !== domain));
    } catch (err: any) {
      alert(`Purchase failed: ${err?.response?.data?.detail || err?.message || 'Registrar not configured — add API keys in Settings.'}`);
    } finally {
      setPurchasing(null);
    }
  };

  const handleConfigureDns = async (domainId: string) => {
    try {
      const resp = await coldOutreachApi.configureDns(domainId, 'sendgrid');
      const r: any = resp?.data || {};
      alert(r.error ? `Auto-configure failed: ${r.error}` : '✅ DNS records pushed to your registrar. Give them a few minutes, then click "Verify DNS Records".');
      await refreshSelectedDomain(domainId);
    } catch (err: any) {
      alert(`Auto-configure DNS failed: ${err?.response?.data?.detail || err?.message || 'Registrar not configured for this domain — add API keys in Settings.'}`);
    }
  };

  const handleAddSuggestionToPool = async (domain: string) => {
    setAddingSuggestion(domain);
    try {
      await coldOutreachApi.addExternalDomain(domain, primaryDomain);
      fetchDomains();
      fetchHealthOverview();
      // Remove from suggestions list
      setSuggestions(prev => prev.filter(s => s.domain !== domain));
    } catch (err) { console.error('Failed to add domain:', err); }
    finally { setAddingSuggestion(null); }
  };

  const handleAddExternalDomain = async () => {
    if (!newDomain || !newPrimaryDomain) return;
    try {
      await coldOutreachApi.addExternalDomain(newDomain, newPrimaryDomain);
      setNewDomain(''); setNewPrimaryDomain('');
      setShowAddDomain(false);
      fetchDomains(); fetchHealthOverview();
    } catch (err) { console.error('Failed to add domain:', err); }
  };

  const handleStartWarmup = async (domainId: string) => {
    try {
      await coldOutreachApi.startWarmup(domainId);
      fetchDomains();
      if (selectedDomain?.id === domainId) {
        const resp = await coldOutreachApi.getDomain(domainId);
        if (resp.data) setSelectedDomain(resp.data);
      }
    } catch (err) { console.error('Failed to start warmup:', err); }
  };

  const handlePauseWarmup = async (domainId: string) => {
    try {
      await coldOutreachApi.pauseWarmup(domainId);
      fetchDomains();
    } catch (err) { console.error('Failed to pause warmup:', err); }
  };

  const handleVerifyDns = async (domainId: string) => {
    try {
      const resp = await coldOutreachApi.verifyDns(domainId);
      if (resp.data) {
        fetchDomains();
        if (selectedDomain?.id === domainId) {
          const domainResp = await coldOutreachApi.getDomain(domainId);
          if (domainResp.data) setSelectedDomain(domainResp.data);
        }
      }
    } catch (err) { console.error('Failed to verify DNS:', err); }
  };

  const refreshSelectedDomain = async (domainId: string) => {
    fetchDomains();
    if (selectedDomain?.id === domainId) {
      const domainResp = await coldOutreachApi.getDomain(domainId);
      if (domainResp.data) setSelectedDomain(domainResp.data);
    }
  };

  const handleAuthenticateSendgrid = async (domainId: string) => {
    try {
      const resp = await coldOutreachApi.authenticateSendgrid(domainId);
      const r: any = resp?.data || {};
      const cnames = r.dns_records?.length ?? 0;
      alert(
        r.status === 'already_authenticated'
          ? '✅ Domain is already authenticated in SendGrid.'
          : `✅ Registered with SendGrid.${cnames ? ` ${cnames} CNAME record(s) added below — publish them at your registrar, then click "Validate SendGrid".` : ' Publish the DNS records below, then click "Validate SendGrid".'}`
      );
      const dnsResp = await coldOutreachApi.getDnsRecords(domainId);
      if (dnsResp.data && selectedDomain?.id === domainId) setDnsRecords(dnsResp.data.dns_records || []);
      await refreshSelectedDomain(domainId);
    } catch (err: any) {
      console.error('SendGrid authenticate failed:', err);
      alert(`SendGrid authentication failed: ${err?.response?.data?.detail || err?.message || 'Unknown error'}`);
    }
  };

  const handleValidateSendgrid = async (domainId: string) => {
    try {
      const resp = await coldOutreachApi.validateSendgrid(domainId);
      const r: any = resp?.data || {};
      alert(
        r.status === 'validated'
          ? '✅ SendGrid domain authentication validated. This domain can now send (and warm up).'
          : `SendGrid validation not complete yet.\n\n${r.message || 'DNS may still be propagating — wait a few minutes and try again.'}`
      );
      await refreshSelectedDomain(domainId);
    } catch (err: any) {
      console.error('SendGrid validate failed:', err);
      alert(`SendGrid validation failed: ${err?.response?.data?.detail || err?.message || 'Unknown error'}`);
    }
  };

  const handleCreateMailbox = async () => {
    if (!selectedDomain || !newMailboxEmail || !newMailboxName) return;
    const requiredSuffix = `@${selectedDomain.domain}`;
    if (!newMailboxEmail.endsWith(requiredSuffix)) {
      alert(`Mailbox email must end with ${requiredSuffix}\n\nThis is a sending identity on your outreach domain. For warmup seed inboxes (Gmail, Outlook, etc.), use the "Warmup Seed Inboxes" section at the top of the Domains tab.`);
      return;
    }
    try {
      await coldOutreachApi.createMailbox(selectedDomain.id, {
        email_address: newMailboxEmail,
        display_name: newMailboxName,
      });
      setNewMailboxEmail(''); setNewMailboxName('');
      const resp = await coldOutreachApi.getMailboxes(selectedDomain.id);
      if (resp.data) setDomainMailboxes(resp.data.mailboxes || []);
      fetchDomains();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to create mailbox';
      alert(detail);
    }
  };

  const handleRemoveDomain = async (domainId: string) => {
    if (!confirm('Remove this domain from the pool? This cannot be undone.')) return;
    try {
      await coldOutreachApi.deleteDomain(domainId);
      setSelectedDomain(null);
      fetchDomains(); fetchHealthOverview();
    } catch (err) { console.error('Failed to remove domain:', err); }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedRecord(id);
    setTimeout(() => setCopiedRecord(null), 2000);
  };

  const getHealthBadge = (score: number) => {
    if (score >= 90) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Excellent ({score})</span>;
    if (score >= 70) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Good ({score})</span>;
    if (score >= 50) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Warning ({score})</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Critical ({score})</span>;
  };

  const getWarmupBadge = (status: string) => {
    const colors: Record<string, string> = {
      not_started: 'bg-gray-100 text-gray-600', warming: 'bg-orange-100 text-orange-800',
      warmed: 'bg-green-100 text-green-800', paused: 'bg-yellow-100 text-yellow-800',
      degraded: 'bg-red-100 text-red-800',
    };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>{status.replace('_', ' ')}</span>;
  };

  const warmupStageInfo: Record<number, { label: string; limit: number }> = {
    1: { label: 'Stage 1 (Days 1-3)', limit: 5 },
    2: { label: 'Stage 2 (Days 4-7)', limit: 15 },
    3: { label: 'Stage 3 (Days 8-14)', limit: 30 },
    4: { label: 'Stage 4 (Days 15-21)', limit: 50 },
    5: { label: 'Stage 5 (Days 22-28)', limit: 75 },
    6: { label: 'Stage 6 (Day 29+)', limit: 100 },
  };

  // If a domain is selected, show detail view
  if (selectedDomain) {
    return (
      <div className="space-y-6">
        <button onClick={() => setSelectedDomain(null)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
          <ChevronRight className="h-4 w-4 rotate-180" /> Back to Domain Pool
        </button>

        {/* Domain Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold">{selectedDomain.domain}</h3>
              <div className="text-sm text-gray-500">Primary: {selectedDomain.primary_domain} | Status: {selectedDomain.purchase_status}</div>
            </div>
            <div className="flex items-center gap-3">
              {getHealthBadge(selectedDomain.health_score || 100)}
              {getWarmupBadge(selectedDomain.warmup_status)}
              {selectedDomain.setup_state === 'failed' && (
                <button onClick={async () => {
                  await coldOutreachApi.resetDomainAgent(selectedDomain.id);
                  const resp = await coldOutreachApi.getDomain(selectedDomain.id);
                  if (resp.data) setSelectedDomain(resp.data);
                }} className="px-3 py-1.5 text-orange-600 border border-orange-200 rounded-lg text-sm hover:bg-orange-50">
                  <RefreshCw className="h-3.5 w-3.5 inline mr-1" /> Retry Setup
                </button>
              )}
              <button onClick={() => handleRemoveDomain(selectedDomain.id)}
                className="px-3 py-1.5 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5 inline mr-1" /> Remove
              </button>
            </div>
          </div>

          {/* Agent Setup Status */}
          {selectedDomain.setup_state && selectedDomain.setup_state !== 'ready' && (
            <div className={`mt-3 p-3 rounded-lg border text-sm ${
              selectedDomain.setup_state === 'failed' ? 'bg-red-50 border-red-200 text-red-700' :
              selectedDomain.setup_state === 'warming' ? 'bg-orange-50 border-orange-200 text-orange-700' :
              'bg-blue-50 border-blue-200 text-blue-700'
            }`}>
              <div className="flex items-center gap-2">
                {selectedDomain.setup_state === 'failed' ? (
                  <XCircle className="h-4 w-4" />
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                <strong>Auto-Setup Agent:</strong>
                <span className="capitalize">{selectedDomain.setup_state.replace('_', ' ')}</span>
                {selectedDomain.setup_error && <span className="text-xs ml-2">({selectedDomain.setup_error})</span>}
              </div>
              <div className="mt-1 flex gap-1">
                {['pending', 'authenticating_sendgrid', 'configuring_dns', 'verifying_dns', 'validating_sendgrid', 'creating_mailbox', 'generating_seeds', 'starting_warmup', 'warming', 'ready'].map((step, i) => {
                  const states = ['pending', 'authenticating_sendgrid', 'configuring_dns', 'verifying_dns', 'validating_sendgrid', 'creating_mailbox', 'generating_seeds', 'starting_warmup', 'warming', 'ready'];
                  const currentIdx = states.indexOf(selectedDomain.setup_state || 'pending');
                  const stepIdx = i;
                  return (
                    <div key={step} className={`flex-1 h-2 rounded-full ${
                      stepIdx < currentIdx ? 'bg-green-400' :
                      stepIdx === currentIdx ? 'bg-blue-400 animate-pulse' :
                      'bg-gray-200'
                    }`} title={step.replace('_', ' ')} />
                  );
                })}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                SendGrid Auth {'->'} DNS {'->'} Verify {'->'} SG Validate {'->'} Mailbox {'->'} Seeds {'->'} Warmup {'->'} Ready
              </div>
            </div>
          )}

          {/* Key metrics */}
          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-lg font-bold">{selectedDomain.daily_sends_today || 0}</div>
              <div className="text-xs text-gray-500">Sends Today / {selectedDomain.daily_limit || 50}</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-lg font-bold">{selectedDomain.delivery_rate != null ? `${selectedDomain.delivery_rate}%` : 'N/A'}</div>
              <div className="text-xs text-gray-500">Delivery Rate</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-lg font-bold">{selectedDomain.bounce_rate != null ? `${selectedDomain.bounce_rate}%` : 'N/A'}</div>
              <div className="text-xs text-gray-500">Bounce Rate</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-lg font-bold">{selectedDomain.mailbox_count || 0}</div>
              <div className="text-xs text-gray-500">Mailboxes</div>
            </div>
          </div>
        </div>

        {/* DNS Records */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h4 className="font-semibold flex items-center gap-2">
              <Shield className="h-5 w-5" /> DNS Configuration
              {selectedDomain.sendgrid_authenticated ? (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">SendGrid authenticated</span>
              ) : (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">SendGrid not authenticated</span>
              )}
            </h4>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => handleAuthenticateSendgrid(selectedDomain.id)}
                title="Register this domain with SendGrid and generate its DKIM CNAME records"
                className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700">
                Authenticate SendGrid
              </button>
              <button onClick={() => handleValidateSendgrid(selectedDomain.id)}
                title="Ask SendGrid to validate the published DNS and mark the domain authenticated"
                className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-sm hover:bg-purple-200">
                Validate SendGrid
              </button>
              {selectedDomain.registrar && selectedDomain.registrar !== 'manual' && (
                <button onClick={() => handleConfigureDns(selectedDomain.id)}
                  title="Push these DNS records to your registrar automatically (requires registrar API keys in Settings)"
                  className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">
                  Auto-configure DNS
                </button>
              )}
              <button onClick={() => handleVerifyDns(selectedDomain.id)}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                Verify DNS Records
              </button>
            </div>
          </div>

          {/* Status badges */}
          <div className="flex gap-4 mb-4">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${selectedDomain.spf_configured ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {selectedDomain.spf_configured ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-500" />}
              <span className="text-sm font-medium">SPF</span>
            </div>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${selectedDomain.dkim_configured ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {selectedDomain.dkim_configured ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-500" />}
              <span className="text-sm font-medium">DKIM</span>
            </div>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${selectedDomain.dmarc_configured ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {selectedDomain.dmarc_configured ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-500" />}
              <span className="text-sm font-medium">DMARC</span>
            </div>
          </div>

          {/* DNS Records to configure */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-700 mb-2">Add these DNS records to your domain registrar:</h5>
            <p className="text-xs text-gray-500 mb-3">
              Enter the <strong>Name/Host</strong> exactly as shown (the registrar-relative label). Most registrars
              (GoDaddy, Namecheap) want the relative label — <code className="bg-gray-100 px-1 rounded">@</code> for the
              root, or e.g. <code className="bg-gray-100 px-1 rounded">em._domainkey</code> — <em>not</em> the full domain.
              Match the <strong>Type</strong> exactly (DKIM must be a <strong>CNAME</strong>, not TXT).
            </p>
            {dnsRecords.length > 0 ? (
              <div className="space-y-3">
                {dnsRecords.map((rec: any, i: number) => {
                  const domainSuffix: string = selectedDomain.domain || '';
                  const relHost = !rec.host || rec.host === domainSuffix
                    ? '@'
                    : (rec.host.endsWith('.' + domainSuffix) ? rec.host.slice(0, -(domainSuffix.length + 1)) : rec.host);
                  return (
                    <div key={i} className="bg-white border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            rec.purpose === 'SPF' ? 'bg-blue-100 text-blue-700' :
                            rec.purpose === 'DKIM' ? 'bg-purple-100 text-purple-700' :
                            'bg-green-100 text-green-700'
                          }`}>{rec.purpose}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            rec.type === 'CNAME' ? 'bg-orange-100 text-orange-700' : 'bg-gray-200 text-gray-700'
                          }`}>{rec.type}</span>
                        </div>
                        <button
                          onClick={() => copyToClipboard(rec.value, `${i}`)}
                          className="text-xs px-2 py-1 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          {copiedRecord === `${i}` ? 'Copied!' : 'Copy Value'}
                        </button>
                      </div>
                      <div className="text-xs text-gray-600 mb-1 flex items-center gap-2">
                        <span><strong>Name/Host:</strong> <code className="bg-gray-100 px-1 rounded">{relHost}</code></span>
                        <button
                          onClick={() => copyToClipboard(relHost, `host-${i}`)}
                          className="text-blue-600 hover:bg-blue-50 rounded px-1"
                        >
                          {copiedRecord === `host-${i}` ? 'Copied!' : 'Copy'}
                        </button>
                        {relHost !== rec.host && (
                          <span className="text-gray-400">(full: <code className="bg-gray-100 px-1 rounded">{rec.host}</code>)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600"><strong>Value:</strong> <code className="bg-gray-100 px-1 rounded break-all">{rec.value}</code></div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No DNS records generated yet. Click "Authenticate SendGrid" (or configure the domain) to generate them.</p>
            )}
          </div>
        </div>

        {/* Warmup Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold flex items-center gap-2">
              <TrendingUp className="h-5 w-5" /> Email Warmup
            </h4>
            <div className="flex gap-2">
              {selectedDomain.warmup_status === 'not_started' && (
                <button onClick={() => handleStartWarmup(selectedDomain.id)}
                  className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700">
                  Start Warmup
                </button>
              )}
              {selectedDomain.warmup_status === 'warming' && (
                <button onClick={() => handlePauseWarmup(selectedDomain.id)}
                  className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-700">
                  Pause Warmup
                </button>
              )}
            </div>
          </div>

          {/* Warmup progress — today's send status at a glance */}
          {warmupStatus && (() => {
            // Hitting the daily cap is the expected, healthy end-state for the
            // day — not a violation. Sending pauses and auto-resumes at the next
            // daily reset, so surface it as a neutral "paused" state rather than
            // the green "Progressing" badge (which reads as "still sending").
            const limitReached =
              warmupStatus.warmup_status === 'warming' &&
              (warmupStatus.daily_limit ?? 0) > 0 &&
              warmupStatus.sent_today >= warmupStatus.daily_limit;
            const progressing = warmupStatus.is_progressing && !limitReached;
            return (
            <div className={`mb-4 rounded-lg border p-3 ${
              warmupStatus.blockers?.length ? 'bg-amber-50 border-amber-200'
              : limitReached ? 'bg-blue-50 border-blue-200'
              : progressing ? 'bg-green-50 border-green-200'
              : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-medium text-gray-800">Warmup Progress</h5>
                <span className={`text-xs font-medium ${
                  warmupStatus.blockers?.length ? 'text-amber-700'
                  : limitReached ? 'text-blue-700'
                  : progressing ? 'text-green-700'
                  : 'text-gray-600'
                }`}>
                  {warmupStatus.blockers?.length
                    ? '● Warming — attention needed'
                    : limitReached
                    ? '● Daily limit reached — paused until reset'
                    : progressing
                    ? '● Progressing'
                    : warmupStatus.warmup_status === 'warming'
                    ? '● Warming'
                    : `● ${String(warmupStatus.warmup_status).replace('_', ' ')}`}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-2">
                <div>
                  <div className="text-gray-500">Sent today</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {warmupStatus.sent_today} / {warmupStatus.daily_limit}
                  </div>
                  <div className="mt-1 h-1.5 w-full rounded bg-gray-200 overflow-hidden">
                    <div
                      className={`h-full rounded ${limitReached ? 'bg-blue-500' : 'bg-orange-500'}`}
                      style={{ width: `${Math.min(100, Math.round((warmupStatus.sent_today / Math.max(1, warmupStatus.daily_limit)) * 100))}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Last send</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {warmupStatus.last_send_at ? new Date(warmupStatus.last_send_at).toLocaleString() : 'never'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Stage</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {warmupStatus.warmup_stage} of 6
                    {warmupStatus.warmup_start_date ? ` (since ${warmupStatus.warmup_start_date})` : ''}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Seed inboxes</div>
                  <div className="text-sm font-semibold text-gray-900">{warmupStatus.seed_inboxes_configured}</div>
                </div>
              </div>
              {limitReached ? (
                <p className="text-[11px] text-blue-700">
                  Daily limit reached — sending is paused and resumes automatically after the next daily reset (midnight, America/Los_Angeles).
                </p>
              ) : warmupStatus.warmup_status === 'warming' && !warmupStatus.blockers?.length ? (
                <p className="text-[11px] text-gray-500">
                  Sends run automatically every {Math.round((warmupStatus.send_interval_seconds || 300) / 60)} minutes until the daily limit is reached.
                </p>
              ) : null}
              {(warmupStatus.blockers || []).map((b: string, i: number) => (
                <p key={i} className="text-[11px] text-amber-700">⚠️ {b}</p>
              ))}
            </div>
            );
          })()}

          {/* Warmup stage progression */}
          <div className="mb-4">
            <h5 className="text-sm font-medium text-gray-700 mb-2">Warmup Stages</h5>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5, 6].map(stage => {
                const currentStage = selectedDomain.warmup_stage || 0;
                const info = warmupStageInfo[stage];
                const isActive = stage === currentStage;
                const isComplete = stage < currentStage;
                return (
                  <div key={stage} className={`flex-1 p-2 rounded text-center text-xs ${
                    isActive ? 'bg-orange-100 border-2 border-orange-400 font-bold' :
                    isComplete ? 'bg-green-100 border border-green-300' :
                    'bg-gray-100 border border-gray-200'
                  }`}>
                    <div>{info.label.split(' ')[0]} {stage}</div>
                    <div className="text-gray-500">{info.limit}/day</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Warmup log table */}
          {warmupLog.length > 0 ? (
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">Daily Warmup Log</h5>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-gray-500 border-b">
                    <th className="pb-1 pr-2">Date</th><th className="pb-1 pr-2">Stage</th>
                    <th className="pb-1 pr-2">Sent</th><th className="pb-1 pr-2">Delivered</th>
                    <th className="pb-1 pr-2">Bounced</th><th className="pb-1 pr-2">Opened</th>
                    <th className="pb-1">Health</th>
                  </tr></thead>
                  <tbody>
                    {warmupLog.map((log: any, i: number) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1 pr-2">{log.log_date}</td>
                        <td className="py-1 pr-2">{log.warmup_stage}</td>
                        <td className="py-1 pr-2">{log.emails_sent}</td>
                        <td className="py-1 pr-2">{log.emails_delivered}</td>
                        <td className="py-1 pr-2 text-red-500">{log.emails_bounced}</td>
                        <td className="py-1 pr-2">{log.emails_opened}</td>
                        <td className="py-1">{log.health_score_snapshot}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-gray-400">
              {selectedDomain.warmup_status === 'not_started'
                ? 'Start warmup to begin tracking daily metrics.'
                : 'No warmup log entries yet. Data will appear after the first day of sending.'}
            </div>
          )}

          {/* Recent individual warmup sends */}
          {warmupSends.length > 0 && (
            <div className="mt-4">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Recent Warmup Sends</h5>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-gray-500 border-b">
                    <th className="pb-1 pr-2">Time</th><th className="pb-1 pr-2">From</th>
                    <th className="pb-1 pr-2">To (seed)</th><th className="pb-1 pr-2">Status</th>
                    <th className="pb-1">Trigger</th>
                  </tr></thead>
                  <tbody>
                    {warmupSends.map((s: any) => (
                      <tr key={s.id} className="border-b last:border-0">
                        <td className="py-1 pr-2 whitespace-nowrap">{new Date(s.sent_at).toLocaleString()}</td>
                        <td className="py-1 pr-2">{s.from_email || '—'}</td>
                        <td className="py-1 pr-2">{s.to_email}</td>
                        <td className="py-1 pr-2">
                          {s.status === 'sent'
                            ? <span className="text-green-600 font-medium">sent</span>
                            : <span className="text-red-600 font-medium" title={s.error || undefined}>failed</span>}
                        </td>
                        <td className="py-1 text-gray-500">{s.triggered_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Warmup Seed Configuration */}
          {selectedDomain.warmup_status !== 'not_started' && (
            <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-sm font-medium text-orange-900">Warmup Seed Inboxes</h5>
                <button
                  disabled={!selectedDomain.sendgrid_authenticated}
                  title={
                    selectedDomain.sendgrid_authenticated
                      ? 'Send warmup emails to your seed inboxes now'
                      : 'Complete SendGrid domain authentication before warmup emails can be sent'
                  }
                  onClick={async () => {
                    try {
                      const resp = await coldOutreachApi.triggerWarmupSends(selectedDomain.id);
                      const r: any = resp?.data || {};
                      const sent = r.emails_sent ?? 0;
                      if (sent > 0) {
                        alert(`✅ Sent ${sent} warmup email${sent === 1 ? '' : 's'} to your seed inboxes.`);
                      } else {
                        alert(`No warmup emails were sent.\n\n${r.message || 'This domain is not ready to send yet.'}`);
                      }
                      const [wResp, stResp, sdResp] = await Promise.all([
                        coldOutreachApi.getWarmupLog(selectedDomain.id, 30),
                        coldOutreachApi.getWarmupStatus(selectedDomain.id).catch(() => null),
                        coldOutreachApi.getWarmupSends(selectedDomain.id, 25).catch(() => null),
                      ]);
                      if (wResp.data) setWarmupLog(wResp.data.warmup_log || []);
                      if (stResp?.data) setWarmupStatus(stResp.data);
                      if (sdResp?.data) setWarmupSends(sdResp.data.sends || []);
                      fetchDomains();
                    } catch (err: any) {
                      console.error('Warmup send failed:', err);
                      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
                      alert(`Warmup send failed: ${detail}`);
                    }
                  }}
                  className={`px-3 py-1 rounded text-xs text-white ${
                    selectedDomain.sendgrid_authenticated
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  <Send className="h-3 w-3 inline mr-1" /> Send Warmup Emails Now
                </button>
              </div>
              <p className="text-xs text-orange-700 mb-2">
                Warmup emails are sent automatically to these seed inboxes to build sender reputation.
                Add email addresses you control across different providers (Gmail, Outlook, Yahoo).
              </p>
              {!selectedDomain.sendgrid_authenticated && (
                <p className="text-xs text-red-700 mb-2">
                  ⚠️ This domain isn't SendGrid-authenticated yet, so warmup sending is disabled.
                  Complete SendGrid domain authentication (Authenticate &amp; Validate SendGrid on this
                  domain) first — the sender only warms up domains that can send DKIM-signed mail from
                  themselves.
                </p>
              )}
              <div className="flex gap-2 mb-2">
                <input type="email" placeholder="seed@example.com"
                  value={newSeedEmail} onChange={e => setNewSeedEmail(e.target.value)}
                  className="flex-1 px-2 py-1 border rounded text-xs" />
                <button
                  onClick={() => {
                    if (newSeedEmail && !seedEmails.includes(newSeedEmail)) {
                      const updated = [...seedEmails, newSeedEmail];
                      setSeedEmails(updated);
                      setNewSeedEmail('');
                      coldOutreachApi.saveWarmupSeedConfig(updated).catch(console.error);
                    }
                  }}
                  disabled={!newSeedEmail}
                  className="px-2 py-1 bg-orange-600 text-white rounded text-xs disabled:opacity-50"
                >
                  <Plus className="h-3 w-3 inline" /> Add
                </button>
              </div>
              {seedEmails.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {seedEmails.map((email, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded text-xs border">
                      {email}
                      <button onClick={() => {
                        const updated = seedEmails.filter((_, j) => j !== i);
                        setSeedEmails(updated);
                        coldOutreachApi.saveWarmupSeedConfig(updated).catch(console.error);
                      }} className="text-red-400 hover:text-red-600">x</button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-orange-500">No seed inboxes configured. Warmup emails will not be sent until seeds are added.</p>
              )}
            </div>
          )}
        </div>

        {/* Mailboxes */}
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="font-semibold mb-1 flex items-center gap-2">
            <Mail className="h-5 w-5" /> Sending Mailboxes
          </h4>
          <p className="text-xs text-gray-500 mb-4">
            Sending identities on <strong>@{selectedDomain.domain}</strong>. These are the "from" addresses used in outreach emails.
            For warmup <em>receiving</em> inboxes (Gmail, Outlook), use the Seed Inboxes section on the Domains tab.
          </p>

          {domainMailboxes.length > 0 && (
            <div className="mb-4">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="pb-1">Email</th><th className="pb-1">Display Name</th>
                  <th className="pb-1">Sends Today</th><th className="pb-1">Status</th>
                </tr></thead>
                <tbody>
                  {domainMailboxes.map((mb: any) => (
                    <tr key={mb.id} className="border-b last:border-0">
                      <td className="py-1 font-mono text-sm">{mb.email_address}</td>
                      <td className="py-1">{mb.display_name}</td>
                      <td className="py-1">{mb.daily_sends_today} / {mb.daily_send_limit}</td>
                      <td className="py-1">{getWarmupBadge(mb.warmup_status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Email Address</label>
              <input type="email" placeholder={`sales@${selectedDomain.domain}`}
                value={newMailboxEmail} onChange={e => setNewMailboxEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Display Name</label>
              <input type="text" placeholder="Sales Team"
                value={newMailboxName} onChange={e => setNewMailboxName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <button onClick={handleCreateMailbox}
              disabled={!newMailboxEmail || !newMailboxName}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap">
              <Plus className="h-4 w-4 inline" /> Add Mailbox
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main list view
  return (
    <div className="space-y-6">
      {/* Health Overview */}
      {healthOverview?.summary && (
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold">{healthOverview.summary.total_domains}</div>
            <div className="text-sm text-gray-500">Total Domains</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{healthOverview.summary.active_domains}</div>
            <div className="text-sm text-gray-500">Active</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">{healthOverview.summary.warming_domains}</div>
            <div className="text-sm text-gray-500">Warming Up</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{healthOverview.summary.degraded_domains}</div>
            <div className="text-sm text-gray-500">Degraded</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold">{healthOverview.summary.avg_health_score}</div>
            <div className="text-sm text-gray-500">Avg Health Score</div>
          </div>
        </div>
      )}

      {/* Warmup Seed Inboxes (Org-Level Config) */}
      <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${seedEmails.length >= 2 ? 'border-green-400' : 'border-orange-400'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <Mail className="h-4 w-4" /> Warmup Seed Inboxes
              {seedEmails.length >= 2 ? (
                <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">{seedEmails.length} configured</span>
              ) : (
                <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">Setup required</span>
              )}
            </h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {seedEmails.length < 2
                ? 'Add at least 2 real email inboxes you control (Gmail, Outlook, Yahoo) to enable domain warmup. The agent will pause until seeds are configured.'
                : 'Warmup emails will be sent to these inboxes to build sender reputation across providers.'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <input type="email" placeholder="your-seed@gmail.com"
            value={newSeedEmail} onChange={e => setNewSeedEmail(e.target.value)}
            className="flex-1 px-3 py-1.5 border rounded-lg text-sm" />
          <button
            onClick={() => {
              if (newSeedEmail && !seedEmails.includes(newSeedEmail)) {
                const updated = [...seedEmails, newSeedEmail];
                setSeedEmails(updated);
                setNewSeedEmail('');
                coldOutreachApi.saveWarmupSeedConfig(updated).catch(console.error);
              }
            }}
            disabled={!newSeedEmail}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            <Plus className="h-4 w-4 inline" /> Add Seed
          </button>
        </div>
        {seedEmails.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {seedEmails.map((email, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs border">
                {email}
                <button onClick={() => {
                  const updated = seedEmails.filter((_, j) => j !== i);
                  setSeedEmails(updated);
                  coldOutreachApi.saveWarmupSeedConfig(updated).catch(console.error);
                }} className="text-red-400 hover:text-red-600 ml-1">x</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Domain Pool */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Globe className="h-5 w-5" /> Domain Pool
          </h3>
          <div className="flex gap-2">
            <button onClick={async () => {
              try {
                await coldOutreachApi.triggerAgentRun();
                fetchDomains(); fetchHealthOverview();
              } catch (err) { console.error('Agent run failed:', err); }
            }} className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 flex items-center gap-1">
              <Zap className="h-4 w-4" /> Run Setup Agent
            </button>
            <button onClick={() => setShowAddDomain(true)} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1">
              <Plus className="h-4 w-4" /> Add Domain Manually
            </button>
          </div>
        </div>

        {/* Generate Look-Alikes */}
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Generate & Procure Look-Alike Domains</h4>
          <p className="text-xs text-blue-700 mb-2">Enter your primary domain to generate similar domains for cold outreach. Add them to your pool with one click.</p>
          <div className="flex gap-2">
            <input type="text" placeholder="Enter your primary domain (e.g., acme.com)"
              value={primaryDomain} onChange={e => setPrimaryDomain(e.target.value)}
              className="flex-1 px-3 py-2 border rounded-lg text-sm" />
            <button onClick={handleGenerateSuggestions} disabled={!primaryDomain}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
              Generate
            </button>
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-blue-700">{suggestions.length} domains generated</span>
                <div className="flex items-center gap-2">
                  <select value={procRegistrar} onChange={e => setProcRegistrar(e.target.value as any)}
                    title="Registrar used for availability check + purchase (configure API keys in Settings → Registrar API Keys)"
                    className="px-2 py-1 border rounded text-xs bg-white">
                    <option value="godaddy">GoDaddy</option>
                    <option value="namecheap">Namecheap</option>
                    <option value="cloudflare">Cloudflare</option>
                  </select>
                  <button onClick={handleCheckAvailability} disabled={checkingAvailability}
                    className="px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1">
                    {checkingAvailability ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                    Check Availability
                  </button>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500 border-b">
                    <th className="pb-1">Domain</th><th className="pb-1">Pattern</th>
                    <th className="pb-1">Availability</th><th className="pb-1 text-right">Action</th>
                  </tr></thead>
                  <tbody>
                    {suggestions.map((s, i) => {
                      const avail = availabilityResults[s.domain];
                      return (
                        <tr key={i} className="border-b last:border-0 hover:bg-blue-100/30">
                          <td className="py-1.5 font-mono text-sm">{s.domain}</td>
                          <td className="py-1.5 text-gray-500 text-xs">{s.pattern_type}</td>
                          <td className="py-1.5">
                            {!avail ? (
                              <span className="text-xs text-gray-400">Not checked</span>
                            ) : avail.available === true ? (
                              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" /> Available
                                {avail.price && <span className="text-gray-500">(${avail.price}/yr)</span>}
                              </span>
                            ) : avail.available === false ? (
                              <span className="text-xs text-red-500 flex items-center gap-1">
                                <XCircle className="h-3 w-3" /> Taken
                              </span>
                            ) : (
                              <span className="text-xs text-yellow-600 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" /> {avail.error || 'Unknown'}
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 text-right">
                            <div className="flex gap-1 justify-end">
                              {avail?.available === true && (
                                <button
                                  onClick={() => handlePurchaseDomain(s.domain)}
                                  disabled={purchasing === s.domain}
                                  className="px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50"
                                  title={`Buy via ${procRegistrar} (requires registrar API keys in Settings)`}
                                >
                                  {purchasing === s.domain ? <Loader2 className="h-3 w-3 animate-spin inline" /> : 'Buy'}
                                </button>
                              )}
                              <button
                                onClick={() => handleAddSuggestionToPool(s.domain)}
                                disabled={addingSuggestion === s.domain}
                                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                title="Add to pool (you already own this domain, or bought it externally)"
                              >
                                {addingSuggestion === s.domain ? (
                                  <Loader2 className="h-3 w-3 animate-spin inline" />
                                ) : (
                                  <><Plus className="h-3 w-3 inline" /> Add</>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                <strong>Buy</strong> purchases an available domain via the selected registrar and adds it to your pool automatically (requires registrar API keys in Settings → Registrar API Keys).
                <strong> Add</strong> registers a domain you already own for DNS setup and warmup.
              </p>
            </div>
          )}
        </div>

        {/* Domain List */}
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : domains.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Globe className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No domains in pool yet. Generate look-alikes above or add a domain manually.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="pb-2">Domain</th><th className="pb-2">Setup</th><th className="pb-2">Health</th>
              <th className="pb-2">DNS</th><th className="pb-2">Sends Today</th><th className="pb-2">Mailboxes</th>
              <th className="pb-2">Actions</th>
            </tr></thead>
            <tbody>
              {domains.map(d => (
                <tr key={d.id} className="border-b last:border-0 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedDomain(d)}>
                  <td className="py-2">
                    <div className="font-medium">{d.domain}</div>
                    <div className="text-xs text-gray-400">{d.primary_domain}</div>
                  </td>
                  <td className="py-2">
                    {(() => {
                      const s = d.setup_state || 'pending';
                      if (s === 'ready') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Ready</span>;
                      if (s === 'failed') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Failed</span>;
                      if (s === 'warming') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Warming</span>;
                      return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{s.replace('_',' ')}</span>;
                    })()}
                  </td>
                  <td className="py-2">
                    <div className="flex gap-1">
                      {d.spf_configured ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : <XCircle className="h-3.5 w-3.5 text-red-400" />}
                      {d.dkim_configured ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : <XCircle className="h-3.5 w-3.5 text-red-400" />}
                      {d.dmarc_configured ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : <XCircle className="h-3.5 w-3.5 text-red-400" />}
                    </div>
                  </td>
                  <td className="py-2">{d.daily_sends_today} / {d.daily_limit || 50}</td>
                  <td className="py-2">{d.mailbox_count}</td>
                  <td className="py-2">
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      {d.warmup_status === 'not_started' && (
                        <button onClick={() => handleStartWarmup(d.id)} className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200">Warmup</button>
                      )}
                      {d.warmup_status === 'warming' && (
                        <button onClick={() => handlePauseWarmup(d.id)} className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200">Pause</button>
                      )}
                      <button onClick={() => handleVerifyDns(d.id)} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                        <Shield className="h-3 w-3 inline" /> DNS
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Domain Dialog */}
      <Dialog open={showAddDomain} onOpenChange={setShowAddDomain}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Existing Domain</DialogTitle>
            <DialogDescription>Add a domain you already own to the outreach pool.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
              <input type="text" placeholder="e.g., getacme.com" value={newDomain} onChange={e => setNewDomain(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Domain (brand)</label>
              <input type="text" placeholder="e.g., acme.com" value={newPrimaryDomain} onChange={e => setNewPrimaryDomain(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setShowAddDomain(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleAddExternalDomain} disabled={!newDomain || !newPrimaryDomain}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Add Domain</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// CALL TASKS TAB
// ============================================================================

function CallTasksTab() {
  const [callTasks, setCallTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [showDisposition, setShowDisposition] = useState(false);
  const [disposition, setDisposition] = useState('');
  const [dispositionNotes, setDispositionNotes] = useState('');
  const [sequenceAction, setSequenceAction] = useState('continue');
  const [callbackAt, setCallbackAt] = useState('');
  const [createOpp, setCreateOpp] = useState(false);
  const [createAsLead, setCreateAsLead] = useState(false);
  const [oppOwner, setOppOwner] = useState('');
  const [oppStage, setOppStage] = useState('lead');
  const [oppAmount, setOppAmount] = useState('');
  const [callMode, setCallMode] = useState<'browser' | 'bridge'>('browser');
  const [repPhone, setRepPhone] = useState('');
  const softphone = useTwilioVoice({ autoRegister: callMode === 'browser' });
  const callBusy = ['connecting', 'ringing', 'open'].includes(softphone.callState.status);
  const [phoneNumbers, setPhoneNumbers] = useState<any[]>([]);

  const fetchCallQueue = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await coldOutreachApi.getMyCallQueue();
      if (resp.data) {
        setCallTasks(resp.data.call_tasks || []);
      }
    } catch (err) {
      console.error('Failed to fetch call queue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPhoneNumbers = useCallback(async () => {
    try {
      const resp = await coldOutreachApi.getPhoneNumbers();
      if (resp.data) setPhoneNumbers(resp.data.phone_numbers || []);
    } catch (err) {
      console.error('Failed to fetch phone numbers:', err);
    }
  }, []);

  useEffect(() => { fetchCallQueue(); fetchPhoneNumbers(); }, [fetchCallQueue, fetchPhoneNumbers]);

  const handleInitiateCall = async (taskId: string) => {
    try {
      const resp = await coldOutreachApi.initiateCall(taskId, callMode, callMode === 'bridge' ? repPhone : undefined);
      if (resp.data) {
        if (callMode === 'browser') {
          const toNumber = resp.data.prospect_phone;
          if (toNumber) {
            await softphone.makeCall(toNumber);
          } else {
            alert('No prospect phone number on this call task.');
          }
        } else {
          alert(`Bridge call initiated. Calling your phone first, then connecting to prospect.`);
        }
        fetchCallQueue();
      }
    } catch (err) {
      console.error('Failed to initiate call:', err);
    }
  };

  const handleDisposition = async () => {
    if (!selectedTask || !disposition) return;
    try {
      await coldOutreachApi.recordDisposition(selectedTask.id, {
        disposition,
        notes: dispositionNotes,
        sequence_action: sequenceAction,
        callback_at: callbackAt ? new Date(callbackAt).toISOString() : undefined,
      });
      if (createOpp) {
        const prospectName = `${selectedTask.prospect_first_name || ''} ${selectedTask.prospect_last_name || ''}`.trim();
        try {
          if (createAsLead) {
            // Promote to a real lead (public.sales_leads)
            await salesApi.createLead({
              name: prospectName || selectedTask.prospect_company || 'Cold outreach lead',
              company: selectedTask.prospect_company || '',
              email: selectedTask.prospect_email || undefined,
              phone: selectedTask.prospect_phone || undefined,
              title: selectedTask.prospect_title || undefined,
              owner: oppOwner || undefined,
              source: 'cold_outreach',
              status: 'qualified',
              notes: dispositionNotes || undefined,
            } as any);
          } else {
            await salesApi.createOpportunity({
              name: selectedTask.prospect_company || prospectName || 'Cold outreach opportunity',
              company: selectedTask.prospect_company || '',
              stage: oppStage,
              owner_name: oppOwner || undefined,
              amount: oppAmount ? parseFloat(oppAmount) : undefined,
              source: 'cold_outreach',
              notes: dispositionNotes || undefined,
            } as any);
          }
        } catch (oppErr) {
          console.error('Lead/opportunity creation failed:', oppErr);
          alert(`Disposition saved, but creating the ${createAsLead ? 'lead' : 'opportunity'} failed.`);
        }
      }
      setShowDisposition(false);
      setDisposition('');
      setDispositionNotes('');
      setSequenceAction('continue');
      setCallbackAt('');
      setCreateOpp(false);
      setCreateAsLead(false);
      setOppOwner('');
      setOppStage('lead');
      setOppAmount('');
      setSelectedTask(null);
      fetchCallQueue();
    } catch (err) {
      console.error('Failed to record disposition:', err);
    }
  };

  const handleSkip = async (taskId: string) => {
    try {
      await coldOutreachApi.skipCall(taskId);
      fetchCallQueue();
    } catch (err) {
      console.error('Failed to skip call:', err);
    }
  };

  const handleAssign = async (taskId: string, toMe: boolean) => {
    try {
      await coldOutreachApi.assignCallTask(taskId, toMe ? { assign_to_me: true } : { assigned_to: null });
      fetchCallQueue();
    } catch (err) {
      console.error('Failed to (un)assign call task:', err);
    }
  };

  const dispositions = [
    { value: 'connected', label: 'Connected', color: 'bg-green-100' },
    { value: 'voicemail', label: 'Voicemail', color: 'bg-yellow-100' },
    { value: 'no_answer', label: 'No Answer', color: 'bg-gray-100' },
    { value: 'busy', label: 'Busy', color: 'bg-orange-100' },
    { value: 'interested', label: 'Interested', color: 'bg-blue-100' },
    { value: 'not_interested', label: 'Not Interested', color: 'bg-red-100' },
    { value: 'callback_requested', label: 'Callback Requested', color: 'bg-purple-100' },
    { value: 'meeting_booked', label: 'Meeting Booked', color: 'bg-green-200' },
    { value: 'wrong_number', label: 'Wrong Number', color: 'bg-gray-200' },
    { value: 'do_not_call', label: 'Do Not Call', color: 'bg-red-200' },
  ];

  return (
    <div className="space-y-6">
      {/* Active browser-call bar */}
      {callBusy && (
        <div className="bg-green-600 text-white rounded-lg shadow p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
            <span className="text-sm font-medium">
              {softphone.callState.status === 'open' ? 'On call'
                : softphone.callState.status === 'ringing' ? 'Ringing…'
                : 'Connecting…'}
              {softphone.callState.remoteNumber ? ` · ${softphone.callState.remoteNumber}` : ''}
            </span>
            {softphone.callState.status === 'open' && (
              <span className="text-sm tabular-nums">
                {Math.floor(softphone.callState.duration / 60).toString().padStart(2, '0')}:
                {(softphone.callState.duration % 60).toString().padStart(2, '0')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={softphone.toggleMute}
              className="px-3 py-1.5 rounded text-sm font-medium bg-white/20 hover:bg-white/30">
              {softphone.callState.muted ? 'Unmute' : 'Mute'}
            </button>
            <button onClick={softphone.hangUp}
              className="px-3 py-1.5 rounded text-sm font-medium bg-red-700 hover:bg-red-800">
              Hang up
            </button>
          </div>
        </div>
      )}
      {!callBusy && softphone.deviceError && callMode === 'browser' && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          Browser calling unavailable: {softphone.deviceError}
        </div>
      )}

      {/* Call Mode Toggle */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Call Mode</h3>
            <p className="text-sm text-gray-500">Choose how to connect calls</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setCallMode('browser')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${callMode === 'browser' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              Call from Browser
            </button>
            <button onClick={() => setCallMode('bridge')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${callMode === 'bridge' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              Call from Phone
            </button>
          </div>
        </div>
        {callMode === 'bridge' && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Phone Number</label>
            <PhoneInput placeholder="+1 (555) 123-4567" value={repPhone} onChange={setRepPhone}
              className="px-3 py-2 border rounded-lg text-sm w-64" />
          </div>
        )}
      </div>

      {/* Call Queue */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">My Call Queue ({callTasks.length})</h3>
          <button onClick={fetchCallQueue} className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : callTasks.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No pending calls in your queue.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {callTasks.map(task => (
              <div key={task.id} className="border rounded-lg p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">
                      {task.prospect_first_name} {task.prospect_last_name}
                      {task.prospect_company && <span className="text-gray-500 font-normal"> at {task.prospect_company}</span>}
                    </div>
                    <div className="text-sm text-gray-500">
                      {task.prospect_phone} {task.prospect_title && `- ${task.prospect_title}`}
                    </div>
                    {task.campaign_name && (
                      <div className="text-xs text-gray-400 mt-1">Campaign: {task.campaign_name}</div>
                    )}
                    <div className="text-xs mt-1">
                      {task.assigned_to
                        ? <span className="text-green-600">● Assigned</span>
                        : <span className="text-gray-400">○ Unassigned</span>}
                    </div>
                    {task.prospect_emails_sent > 0 && (
                      <div className="text-xs text-blue-500 mt-1">
                        Emails: {task.prospect_emails_sent} sent, {task.prospect_emails_opened || 0} opened, {task.prospect_emails_clicked || 0} clicked
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleInitiateCall(task.id)}
                      disabled={callMode === 'bridge' && !repPhone}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      <Play className="h-4 w-4" /> Call
                    </button>
                    <button
                      onClick={() => { setSelectedTask(task); setShowDisposition(true); }}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                    >
                      Dispose
                    </button>
                    {task.assigned_to ? (
                      <button onClick={() => handleAssign(task.id, false)}
                        className="px-3 py-2 text-gray-400 hover:text-gray-600 text-sm">
                        Unassign
                      </button>
                    ) : (
                      <button onClick={() => handleAssign(task.id, true)}
                        className="px-3 py-2 text-blue-600 hover:text-blue-800 text-sm">
                        Claim
                      </button>
                    )}
                    <button onClick={() => handleSkip(task.id)}
                      className="px-3 py-2 text-gray-400 hover:text-gray-600 text-sm">
                      Skip
                    </button>
                  </div>
                </div>
                {task.call_script && (
                  <div className="mt-2 p-2 bg-yellow-50 rounded text-sm text-gray-700 border-l-2 border-yellow-400">
                    <strong>Script:</strong> {task.call_script.substring(0, 150)}{task.call_script.length > 150 ? '...' : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phone Numbers */}
      {phoneNumbers.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-3">Outreach Phone Numbers</h3>
          <div className="grid grid-cols-3 gap-3">
            {phoneNumbers.map(pn => (
              <div key={pn.id} className="p-3 border rounded-lg text-sm">
                <div className="font-mono font-medium">{pn.phone_number}</div>
                <div className="text-gray-500">{pn.area_code && `Area: ${pn.area_code}`} {pn.campaign_name && `- ${pn.campaign_name}`}</div>
                <div className="text-xs text-gray-400 mt-1">Calls today: {pn.daily_calls_today} / {pn.daily_call_limit}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disposition Dialog */}
      <Dialog open={showDisposition} onOpenChange={setShowDisposition}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Call Disposition</DialogTitle>
            <DialogDescription>
              {selectedTask && `${selectedTask.prospect_first_name} ${selectedTask.prospect_last_name} - ${selectedTask.prospect_phone}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Disposition</label>
              <div className="grid grid-cols-2 gap-2">
                {dispositions.map(d => (
                  <button key={d.value} onClick={() => setDisposition(d.value)}
                    className={`p-2 rounded-lg text-sm text-left transition-colors ${disposition === d.value ? 'ring-2 ring-blue-500 ' + d.color : d.color + ' opacity-60 hover:opacity-100'}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={dispositionNotes} onChange={e => setDispositionNotes(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" rows={3} placeholder="Call notes..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sequence Action</label>
              <select value={sequenceAction} onChange={e => setSequenceAction(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="continue">Continue Sequence</option>
                <option value="pause">Pause Sequence</option>
                <option value="complete">Complete Sequence</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Follow-up date/time{disposition === 'callback_requested' && <span className="text-red-500"> — set a time to schedule the callback</span>}
              </label>
              <input type="datetime-local" value={callbackAt} onChange={e => setCallbackAt(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
              <p className="text-xs text-gray-400 mt-1">Scheduling a follow-up creates a new call task for this prospect at that time.</p>
            </div>
            <div className="border-t pt-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={createOpp} onChange={e => setCreateOpp(e.target.checked)} />
                Create a pipeline record from this call
              </label>
              {createOpp && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div className="col-span-2 flex items-center gap-4 text-sm">
                    <label className="flex items-center gap-1">
                      <input type="radio" name="createType" checked={!createAsLead} onChange={() => setCreateAsLead(false)} />
                      Opportunity
                    </label>
                    <label className="flex items-center gap-1">
                      <input type="radio" name="createType" checked={createAsLead} onChange={() => setCreateAsLead(true)} />
                      Lead
                    </label>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Owner (assign to a rep)</label>
                    <input value={oppOwner} onChange={e => setOppOwner(e.target.value)} placeholder="Owner name"
                      className="w-full px-2 py-1.5 border rounded text-sm" />
                  </div>
                  {!createAsLead && (
                    <>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Stage</label>
                        <select value={oppStage} onChange={e => setOppStage(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm">
                          <option value="lead">Lead</option>
                          <option value="qualification">Qualification</option>
                          <option value="discovery">Discovery</option>
                          <option value="proposal">Proposal</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Amount (optional)</label>
                        <input type="number" value={oppAmount} onChange={e => setOppAmount(e.target.value)} placeholder="0"
                          className="w-full px-2 py-1.5 border rounded text-sm" />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setShowDisposition(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleDisposition} disabled={!disposition}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Save Disposition</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// CAMPAIGN DETAIL VIEW
// ============================================================================

interface CampaignDetailViewProps {
  campaign: ColdOutreachCampaign;
  onBack: () => void;
  onUpdate: () => void;
}

function CampaignDetailView({ campaign, onBack, onUpdate }: CampaignDetailViewProps) {
  const isTriveny = productConfig.isTriveny;
  const [activeTab, setActiveTab] = useState<CampaignDetailTab>('overview');
  const [steps, setSteps] = useState<SequenceStep[]>([]);
  const [prospects, setProspects] = useState<ColdOutreachProspect[]>([]);
  const [activities, setActivities] = useState<ColdOutreachActivity[]>([]);
  const [replies, setReplies] = useState<ColdOutreachReply[]>([]);
  const [analytics, setAnalytics] = useState<CampaignAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAddStepModal, setShowAddStepModal] = useState(false);
  const [showAddProspectModal, setShowAddProspectModal] = useState(false);
  const [sendingProspectId, setSendingProspectId] = useState<string | null>(null);
  const [briefStatus, setBriefStatus] = useState<Record<string, { status: string; followup_status?: string | null; landing_response?: string | null }>>({});
  const [briefProspect, setBriefProspect] = useState<ColdOutreachProspect | null>(null);
  const [personalizationCost, setPersonalizationCost] = useState<number | null>(null);
  const personalizationOn = (campaign.personalization_mode || 'off') !== 'off';

  const fetchBriefs = useCallback(async () => {
    if ((campaign.personalization_mode || 'off') === 'off') return;
    const response = await coldOutreachApi.getBriefs(campaign.id);
    if (response.success && response.data) {
      const map: Record<string, { status: string; followup_status?: string | null; landing_response?: string | null }> = {};
      response.data.briefs.forEach((b) => {
        map[b.prospect_id] = { status: b.status, followup_status: b.followup_status, landing_response: b.landing_response };
      });
      setBriefStatus(map);
      setPersonalizationCost(response.data.total_cost_usd ?? null);
    }
  }, [campaign.id, campaign.personalization_mode]);

  const handleSendNow = async (prospect: ColdOutreachProspect) => {
    const hasEmailStep = steps.some(s => s.step_type ? s.step_type === 'email' : true);
    if (!hasEmailStep && steps.length === 0) {
      alert('Add an email step to the sequence first — the single send uses the campaign\'s first email step as its content.');
      return;
    }
    if (!confirm(`Send the campaign's first email to ${prospect.email} now?`)) return;
    setSendingProspectId(prospect.id);
    try {
      const resp = await coldOutreachApi.sendProspectNow(campaign.id, prospect.id);
      if (resp.success && resp.data) {
        alert(`✅ Sent "${resp.data.subject}" to ${resp.data.to_email} from ${resp.data.from_email}.`);
        fetchProspects();
      } else {
        alert(`Send failed: ${resp.error || 'unknown error'}`);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      alert(`Send failed: ${detail}`);
    } finally {
      setSendingProspectId(null);
    }
  };

  // Fetch campaign data
  const fetchSteps = useCallback(async () => {
    const response = await coldOutreachApi.getSteps(campaign.id);
    if (response.success && response.data) {
      setSteps(response.data);
    }
  }, [campaign.id]);

  const fetchProspects = useCallback(async () => {
    const response = await coldOutreachApi.getProspects(campaign.id, { page_size: 100 });
    if (response.success && response.data) {
      setProspects(response.data.prospects || []);
    }
  }, [campaign.id]);

  const fetchActivities = useCallback(async () => {
    const response = await coldOutreachApi.getActivities(campaign.id, { page_size: 50 });
    if (response.success && response.data) {
      setActivities(response.data.activities || []);
    }
  }, [campaign.id]);

  const fetchReplies = useCallback(async () => {
    const response = await coldOutreachApi.getReplies(campaign.id, { page_size: 50 });
    if (response.success && response.data) {
      setReplies(response.data.replies || []);
    }
  }, [campaign.id]);

  const fetchAnalytics = useCallback(async () => {
    const response = await coldOutreachApi.getCampaignAnalytics(campaign.id);
    if (response.success && response.data) {
      setAnalytics(response.data);
    }
  }, [campaign.id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSteps(), fetchProspects(), fetchBriefs()]).then(() => setLoading(false));
  }, [fetchSteps, fetchProspects, fetchBriefs]);

  useEffect(() => {
    switch (activeTab) {
      case 'activity':
        fetchActivities();
        break;
      case 'replies':
        fetchReplies();
        break;
      case 'analytics':
        fetchAnalytics();
        break;
    }
  }, [activeTab, fetchActivities, fetchReplies, fetchAnalytics]);

  // Handle campaign actions
  const handleStartCampaign = async () => {
    const response = await coldOutreachApi.startCampaign(campaign.id);
    if (response.success) {
      onUpdate();
    }
  };

  const handlePauseCampaign = async () => {
    const response = await coldOutreachApi.pauseCampaign(campaign.id);
    if (response.success) {
      onUpdate();
    }
  };

  const handleAddStep = async (step: Partial<SequenceStep>) => {
    const response = await coldOutreachApi.createStep(campaign.id, step);
    if (response.success) {
      fetchSteps();
    }
  };

  const handleAddProspect = async (prospect: Partial<ColdOutreachProspect>) => {
    const response = await coldOutreachApi.addProspect(campaign.id, prospect);
    if (response.success) {
      fetchProspects();
      onUpdate();
    }
  };

  return (
    <div
      className={isTriveny ? 'triveny-platform' : undefined}
      style={
        isTriveny
          ? { minHeight: '100vh', background: tvTokens.paper, fontFamily: tvTokens.fontSans, color: tvTokens.ink }
          : undefined
      }
    >
      {/* Header */}
      <div className="bg-white border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button onClick={onBack} className="text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1">
            <ChevronDown className="h-4 w-4 rotate-90" /> Back to Campaigns
          </button>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                {campaign.name}
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(campaign.status)}`}>
                  {campaign.status}
                </span>
              </h2>
              <p className="text-gray-500 mt-1">{campaign.description}</p>
            </div>
            <div className="flex items-center gap-2">
              {campaign.status === 'draft' && (
                <button
                  onClick={handleStartCampaign}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  disabled={steps.length === 0 || prospects.length === 0}
                >
                  <Play className="h-4 w-4" /> Start Campaign
                </button>
              )}
              {campaign.status === 'active' && (
                <button
                  onClick={handlePauseCampaign}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
                >
                  <Pause className="h-4 w-4" /> Pause
                </button>
              )}
              {campaign.status === 'paused' && (
                <button
                  onClick={handleStartCampaign}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Play className="h-4 w-4" /> Resume
                </button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{campaign.total_prospects}</div>
              <div className="text-xs text-gray-500">Prospects</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{campaign.prospects_contacted}</div>
              <div className="text-xs text-gray-500">Contacted</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{campaign.prospects_opened}</div>
              <div className="text-xs text-gray-500">Opened</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{campaign.prospects_clicked}</div>
              <div className="text-xs text-gray-500">Clicked</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{campaign.prospects_replied}</div>
              <div className="text-xs text-gray-500">Replied</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{campaign.meetings_booked}</div>
              <div className="text-xs text-gray-500">Meetings</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 mt-6">
            {(['overview', 'sequence', 'prospects', 'activity', 'replies', 'analytics'] as CampaignDetailTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                  activeTab === tab
                    ? 'bg-gray-100 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lg:col-span-2">
              <CampaignPersonalizationSettings campaign={campaign} onSaved={onUpdate} />
            </div>
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="font-semibold mb-4">Campaign Settings</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">From</span>
                  <span>{campaign.from_name} {'<'}{campaign.from_email}{'>'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Daily Limit</span>
                  <span>{campaign.daily_send_limit} emails</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Sending Window</span>
                  <span>{campaign.sending_start_time} - {campaign.sending_end_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Timezone</span>
                  <span>{campaign.timezone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Track Opens</span>
                  <span>{campaign.track_opens ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Track Clicks</span>
                  <span>{campaign.track_clicks ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Performance</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Open Rate</span>
                    <span>{formatPercentage(campaign.prospects_opened, campaign.prospects_contacted)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: formatPercentage(campaign.prospects_opened, campaign.prospects_contacted) }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Click Rate</span>
                    <span>{formatPercentage(campaign.prospects_clicked, campaign.prospects_contacted)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: formatPercentage(campaign.prospects_clicked, campaign.prospects_contacted) }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Reply Rate</span>
                    <span>{formatPercentage(campaign.prospects_replied, campaign.prospects_contacted)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-orange-600 h-2 rounded-full"
                      style={{ width: formatPercentage(campaign.prospects_replied, campaign.prospects_contacted) }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sequence Tab */}
        {activeTab === 'sequence' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">Email Sequence</h3>
              <button
                onClick={() => setShowAddStepModal(true)}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"
              >
                <Plus className="h-4 w-4" /> Add Step
              </button>
            </div>

            {steps.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Steps Yet</h3>
                <p className="text-gray-500 mb-4">Add email steps to build your sequence.</p>
                <button
                  onClick={() => setShowAddStepModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add First Step
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {steps.map((step, index) => (
                  <div key={step.id} className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                          {step.step_number}
                        </div>
                        <div>
                          <div className="font-medium">{step.name || `Step ${step.step_number}`}</div>
                          {step.delay_days > 0 || step.delay_hours > 0 ? (
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Wait {step.delay_days}d {step.delay_hours}h
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">Sends immediately</div>
                          )}
                          {step.subject_template && (
                            <div className="text-sm text-gray-700 mt-2">
                              <strong>Subject:</strong> {step.subject_template}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{step.total_sent}</div>
                          <div>Sent</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-green-600">{step.total_opened}</div>
                          <div>Opened</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-purple-600">{step.total_clicked}</div>
                          <div>Clicked</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-orange-600">{step.total_replied}</div>
                          <div>Replied</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Prospects Tab */}
        {activeTab === 'prospects' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">
                {prospects.length} Prospects
                {personalizationOn && personalizationCost !== null && (
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    · Personalization spend {formatUsd(personalizationCost)}
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddProspectModal(true)}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"
                >
                  <UserPlus className="h-4 w-4" /> Add Prospect
                </button>
                <button className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1">
                  <Upload className="h-4 w-4" /> Import CSV
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prospect</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    {personalizationOn && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Personalized</th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Step</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Contact</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {prospects.map(prospect => (
                    <tr key={prospect.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {prospect.first_name} {prospect.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{prospect.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{prospect.company_name || '-'}</div>
                        <div className="text-sm text-gray-500">{prospect.job_title || '-'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(prospect.status)}`}>
                          {prospect.status}
                        </span>
                      </td>
                      {personalizationOn && (
                        <td className="px-6 py-4">
                          <div className="flex flex-col items-start gap-1">
                            <BriefStatusChip status={briefStatus[prospect.id]?.status || 'pending'} />
                            {briefStatus[prospect.id]?.landing_response && (
                              <span className="text-[11px] text-green-700">Responded on their page</span>
                            )}
                            {briefStatus[prospect.id]?.followup_status === 'drafted' && (
                              <span className="text-[11px] text-amber-700">Follow-up ready</span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm text-gray-500">
                        Step {prospect.current_step}/{steps.length}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="flex items-center gap-1">
                            <Send className="h-3 w-3" /> {prospect.total_emails_sent}
                          </span>
                          <span className="flex items-center gap-1 text-green-600">
                            <Eye className="h-3 w-3" /> {prospect.total_opens}
                          </span>
                          <span className="flex items-center gap-1 text-purple-600">
                            <MousePointer className="h-3 w-3" /> {prospect.total_clicks}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {prospect.last_contacted_at ? formatTimeAgo(prospect.last_contacted_at) : 'Never'}
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        {personalizationOn && (
                          <button
                            onClick={() => setBriefProspect(prospect)}
                            className="mr-2 px-2 py-1 text-xs font-medium text-purple-700 border border-purple-200 rounded hover:bg-purple-50"
                            title="Research, draft and their landing page"
                          >
                            Brief
                          </button>
                        )}
                        {!['unsubscribed', 'bounced', 'removed'].includes(prospect.status) && (
                          <button
                            onClick={() => handleSendNow(prospect)}
                            disabled={sendingProspectId === prospect.id}
                            className="px-2 py-1 text-xs font-medium text-blue-700 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50"
                            title="Send the campaign's first email to this prospect immediately"
                          >
                            {sendingProspectId === prospect.id ? 'Sending…' : 'Send now'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Recent Activity</h3>
            </div>
            <div className="divide-y">
              {activities.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  No activity yet
                </div>
              ) : (
                activities.map(activity => (
                  <div key={activity.id} className="p-4 flex items-start gap-4">
                    <div className={`p-2 rounded-full ${
                      activity.activity_type.includes('open') ? 'bg-green-100' :
                      activity.activity_type.includes('click') ? 'bg-purple-100' :
                      activity.activity_type.includes('reply') ? 'bg-orange-100' :
                      'bg-blue-100'
                    }`}>
                      {activity.activity_type.includes('sent') && <Send className="h-4 w-4 text-blue-600" />}
                      {activity.activity_type.includes('open') && <Eye className="h-4 w-4 text-green-600" />}
                      {activity.activity_type.includes('click') && <MousePointer className="h-4 w-4 text-purple-600" />}
                      {activity.activity_type.includes('reply') && <MessageSquare className="h-4 w-4 text-orange-600" />}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {activity.activity_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      {activity.subject && (
                        <div className="text-sm text-gray-500">{activity.subject}</div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{formatTimeAgo(activity.created_at)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Replies Tab */}
        {activeTab === 'replies' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Replies</h3>
            </div>
            <div className="divide-y">
              {replies.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  No replies yet
                </div>
              ) : (
                replies.map(reply => (
                  <div key={reply.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-medium">{reply.from_email}</div>
                        <div className="text-sm text-gray-500">{reply.subject}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {reply.classification && (
                          <span className={`px-2 py-1 text-xs rounded-full ${getReplyColor(reply.classification)}`}>
                            {reply.classification}
                          </span>
                        )}
                        <span className="text-xs text-gray-500">{formatTimeAgo(reply.received_at)}</span>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-gray-600 bg-gray-50 p-3 rounded">
                      {reply.body_preview}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">Open Rate</div>
                <div className="text-2xl font-bold text-green-600">{Math.round(analytics.open_rate * 100)}%</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">Click Rate</div>
                <div className="text-2xl font-bold text-purple-600">{Math.round(analytics.click_rate * 100)}%</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">Reply Rate</div>
                <div className="text-2xl font-bold text-orange-600">{Math.round(analytics.reply_rate * 100)}%</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-500">Bounce Rate</div>
                <div className="text-2xl font-bold text-red-600">{Math.round(analytics.bounce_rate * 100)}%</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Step Performance</h3>
              <div className="space-y-3">
                {analytics.step_performance.map(step => (
                  <div key={step.step_number} className="flex items-center gap-4">
                    <div className="w-20 text-sm font-medium">Step {step.step_number}</div>
                    <div className="flex-1 flex items-center gap-2 text-sm">
                      <span>{step.sent} sent</span>
                      <span className="text-green-600">{step.opened} opened</span>
                      <span className="text-purple-600">{step.clicked} clicked</span>
                      <span className="text-orange-600">{step.replied} replied</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddStepModal
        isOpen={showAddStepModal}
        onClose={() => setShowAddStepModal(false)}
        onSubmit={handleAddStep}
        stepNumber={steps.length + 1}
      />
      <AddProspectModal
        isOpen={showAddProspectModal}
        onClose={() => setShowAddProspectModal(false)}
        onSubmit={handleAddProspect}
      />
      <ProspectBriefDialog
        campaignId={campaign.id}
        prospect={briefProspect}
        open={briefProspect !== null}
        onClose={() => setBriefProspect(null)}
        onChanged={fetchBriefs}
      />
    </div>
  );
}

// ============================================================================
// REGISTRAR API-KEY CONFIG (Settings tab)
// ============================================================================

function RegistrarConfigCard() {
  const [registrars, setRegistrars] = useState<any[]>([]);
  const [registrar, setRegistrar] = useState<'godaddy' | 'namecheap' | 'cloudflare'>('godaddy');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const resp = await coldOutreachApi.getRegistrars();
      setRegistrars(resp.data?.registrars || []);
    } catch (err) { console.error('Failed to load registrars:', err); }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!apiKey) { alert('API key is required.'); return; }
    const creds: Record<string, string> = { api_key: apiKey };
    if (registrar !== 'cloudflare') creds.api_secret = apiSecret;
    setSaving(true);
    try {
      await coldOutreachApi.saveRegistrar(registrar, creds);
      alert(`✅ Saved ${registrar} API credentials. You can now check availability and purchase domains from the Domains tab.`);
      setApiKey(''); setApiSecret('');
      load();
    } catch (err: any) {
      alert(`Failed to save registrar credentials: ${err?.response?.data?.detail || err?.message || 'Unknown error'}`);
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-semibold mb-4 flex items-center gap-2">
        <Settings className="h-5 w-5" /> Registrar API Keys
      </h3>
      <p className="text-gray-600 mb-4 text-sm">
        Connect a domain registrar to check availability, purchase look-alike domains, and auto-configure DNS
        directly from the Domains tab. Credentials are stored encrypted and never shown back.
      </p>
      {registrars.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {registrars.map((r: any, i: number) => (
            <span key={i} className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
              {r.registrar} configured{r.is_active === false ? ' (inactive)' : ''}
            </span>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Registrar</label>
          <select value={registrar} onChange={e => setRegistrar(e.target.value as any)}
            className="w-full px-2 py-1.5 border rounded text-sm">
            <option value="godaddy">GoDaddy</option>
            <option value="namecheap">Namecheap</option>
            <option value="cloudflare">Cloudflare</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">API Key</label>
          <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
            placeholder="API key" className="w-full px-2 py-1.5 border rounded text-sm" />
        </div>
        {registrar !== 'cloudflare' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">API Secret</label>
            <input type="password" value={apiSecret} onChange={e => setApiSecret(e.target.value)}
              placeholder="API secret" className="w-full px-2 py-1.5 border rounded text-sm" />
          </div>
        )}
      </div>
      <button onClick={handleSave} disabled={saving}
        className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
        {saving ? 'Saving…' : 'Save Registrar Credentials'}
      </button>
    </div>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export function ColdOutreachPage() {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const isTriveny = productConfig.isTriveny;
  const [activeTab, setActiveTab] = useState<PageTab>('campaigns');
  const [campaigns, setCampaigns] = useState<ColdOutreachCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<ColdOutreachCampaign | null>(null);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [showSmtpModal, setShowSmtpModal] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    const response = await coldOutreachApi.getCampaigns({
      status: statusFilter as CampaignStatus || undefined,
      page: 1,
      page_size: 50,
    });
    if (response.success && response.data) {
      setCampaigns(response.data.campaigns || []);
      setTotalCampaigns(response.data.total || 0);
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Handle create campaign
  const handleCreateCampaign = async (campaignData: Record<string, any>) => {
    const response = await coldOutreachApi.createCampaign(campaignData);
    if (response.success) {
      const created: any = response.data;
      if (created?.total_prospects > 0) {
        alert(`Campaign created with ${created.total_prospects} prospect${created.total_prospects === 1 ? '' : 's'} enrolled from the selected list.`);
      }
      fetchCampaigns();
    } else {
      alert(`Failed to create campaign: ${response.error || 'unknown error'}`);
    }
  };

  // Filter campaigns by search
  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate stats
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const totalProspects = campaigns.reduce((sum, c) => sum + c.total_prospects, 0);
  const totalReplies = campaigns.reduce((sum, c) => sum + c.prospects_replied, 0);
  const totalMeetings = campaigns.reduce((sum, c) => sum + c.meetings_booked, 0);

  // If campaign is selected, show detail view
  if (selectedCampaign) {
    return (
      <CampaignDetailView
        campaign={selectedCampaign}
        onBack={() => setSelectedCampaign(null)}
        onUpdate={() => {
          fetchCampaigns();
          // Refresh selected campaign
          coldOutreachApi.getCampaign(selectedCampaign.id).then(response => {
            if (response.success && response.data) {
              setSelectedCampaign(response.data);
            }
          });
        }}
      />
    );
  }

  const coldOutreachStats: Array<{ label: string; value: number | string; Icon: any }> = [
    { label: 'Active campaigns', value: activeCampaigns, Icon: Mail },
    { label: 'Total prospects', value: totalProspects, Icon: Users },
    { label: 'Total replies', value: totalReplies, Icon: MessageSquare },
    { label: 'Meetings booked', value: totalMeetings, Icon: Calendar },
  ];
  const coldOutreachTabs: PageTab[] = ['campaigns', 'domains', 'calls', 'templates', 'settings'];

  return (
    <div
      className={isTriveny ? 'triveny-platform' : 'min-h-screen bg-gray-50'}
      style={
        isTriveny
          ? {
              minHeight: '100vh',
              background: tvTokens.paper,
              fontFamily: tvTokens.fontSans,
              color: tvTokens.ink,
            }
          : undefined
      }
    >
      {isTriveny ? (
        <>
          <TvPageBar
            eyebrow="Market · Outreach"
            title={t('platform.coldOutreach.title', 'Cold Outreach')}
            actions={
              <TvButton variant="ink" icon={Plus} onClick={() => setShowCreateModal(true)}>
                {t('platform.coldOutreach.newCampaign', 'New Campaign')}
              </TvButton>
            }
          />
          <div style={{ padding: '20px 22px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
                marginBottom: 18,
              }}
            >
              {coldOutreachStats.map(({ label, value, Icon }) => (
                <div
                  key={label}
                  style={{
                    background: tvTokens.surface,
                    border: `1px solid ${tvTokens.border}`,
                    borderRadius: tvTokens.rLg,
                    padding: '14px 16px',
                    boxShadow: tvTokens.shadow1,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontFamily: tvTokens.fontMono,
                      fontSize: 10,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: tvTokens.ink500,
                      marginBottom: 6,
                    }}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </div>
                  <div
                    style={{
                      fontFamily: tvTokens.fontDisplay,
                      fontSize: 26,
                      fontWeight: 600,
                      letterSpacing: '-0.025em',
                      color: tvTokens.ink,
                    }}
                  >
                    {value}
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                gap: 4,
                marginBottom: 16,
                borderBottom: `1px solid ${tvTokens.borderMuted}`,
              }}
            >
              {coldOutreachTabs.map(tab => {
                const active = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: '8px 14px',
                      fontFamily: tvTokens.fontDisplay,
                      fontSize: 13,
                      fontWeight: 600,
                      color: active ? tvTokens.ink : tvTokens.ink500,
                      background: 'transparent',
                      border: 0,
                      borderBottom: `2px solid ${active ? tvTokens.accent : 'transparent'}`,
                      cursor: 'pointer',
                      transition: 'color .12s ease, border-color .12s ease',
                      textTransform: 'capitalize',
                    }}
                    aria-current={active ? 'page' : undefined}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                {!tabbed && (
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Send className="h-6 w-6 text-blue-600" />
                  {t('platform.coldOutreach.title', 'Cold Outreach')}
                </h1>
                )}
                <p className="text-gray-500 mt-1">
                  {t('platform.coldOutreach.description', 'Create and manage cold email sequences to reach new prospects')}
                </p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                {t('platform.coldOutreach.newCampaign', 'New Campaign')}
              </button>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-gray-600">{t('platform.coldOutreach.stats.activeCampaigns', 'Active Campaigns')}</span>
                </div>
                <div className="text-2xl font-bold text-blue-600 mt-1">{activeCampaigns}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-gray-600">{t('platform.coldOutreach.stats.totalProspects', 'Total Prospects')}</span>
                </div>
                <div className="text-2xl font-bold text-green-600 mt-1">{totalProspects}</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-orange-600" />
                  <span className="text-sm text-gray-600">{t('platform.coldOutreach.stats.totalReplies', 'Total Replies')}</span>
                </div>
                <div className="text-2xl font-bold text-orange-600 mt-1">{totalReplies}</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-purple-600" />
                  <span className="text-sm text-gray-600">{t('platform.coldOutreach.stats.meetingsBooked', 'Meetings Booked')}</span>
                </div>
                <div className="text-2xl font-bold text-purple-600 mt-1">{totalMeetings}</div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-1 mt-6 border-b">
              {(['campaigns', 'domains', 'calls', 'templates', 'settings'] as PageTab[]).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === tab
                      ? 'bg-white text-blue-600 border border-b-white -mb-px'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <>
            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg"
              >
                <option value="">{t('platform.coldOutreach.allStatuses', 'All Statuses')}</option>
                <option value="draft">{t('platform.coldOutreach.status.draft', 'Draft')}</option>
                <option value="active">{t('platform.coldOutreach.status.active', 'Active')}</option>
                <option value="paused">{t('platform.coldOutreach.status.paused', 'Paused')}</option>
                <option value="completed">{t('platform.coldOutreach.status.completed', 'Completed')}</option>
              </select>
              <button
                onClick={fetchCampaigns}
                className="p-2 border rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Campaigns List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('platform.coldOutreach.noCampaigns', 'No Campaigns Yet')}</h3>
                <p className="text-gray-500 mb-4">{t('platform.coldOutreach.noCampaignsDescription', 'Create your first cold outreach campaign to start reaching prospects.')}</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('platform.coldOutreach.createCampaign', 'Create Campaign')}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCampaigns.map(campaign => (
                  <div
                    key={campaign.id}
                    className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedCampaign(campaign)}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {campaign.name}
                            <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(campaign.status)}`}>
                              {campaign.status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {campaign.from_name} {'<'}{campaign.from_email}{'>'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 sm:gap-6 text-sm">
                        <div className="text-center">
                          <div className="font-semibold text-gray-900">{campaign.total_prospects}</div>
                          <div className="text-gray-500">Prospects</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-green-600">
                            {campaign.prospects_contacted > 0
                              ? Math.round((campaign.prospects_opened / campaign.prospects_contacted) * 100)
                              : 0}%
                          </div>
                          <div className="text-gray-500">Open Rate</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-orange-600">{campaign.prospects_replied}</div>
                          <div className="text-gray-500">Replies</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-purple-600">{campaign.meetings_booked}</div>
                          <div className="text-gray-500">Meetings</div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <CampaignTemplates />
        )}

        {/* Domains Tab */}
        {activeTab === 'domains' && (
          <div className="space-y-6">
            <DomainPoolTab />
          </div>
        )}

        {/* Calls Tab */}
        {activeTab === 'calls' && (
          <div className="space-y-6">
            <CallTasksTab />
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5" /> Email Configuration
              </h3>
              <p className="text-gray-600 mb-4">
                Configure your email sending settings and connect your email provider.
              </p>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">SMTP Configuration</div>
                    <div className="text-sm text-gray-500">Connect your email server for sending</div>
                  </div>
                  <button
                    onClick={() => setShowSmtpModal(true)}
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-white"
                  >
                    Configure
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">Domain Verification &amp; Warmup</div>
                    <div className="text-sm text-gray-500">Add sending domains, publish DNS, authenticate SendGrid, and warm up</div>
                  </div>
                  <button
                    onClick={() => setActiveTab('domains')}
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-white"
                  >
                    Go to Domains
                  </button>
                </div>
              </div>
            </div>

            <RegistrarConfigCard />

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5" /> Deliverability
              </h3>
              <p className="text-gray-600">
                Monitor your email deliverability and domain health.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateCampaignModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateCampaign}
      />
      <SmtpConfigModal
        isOpen={showSmtpModal}
        onClose={() => setShowSmtpModal(false)}
        onSave={(config) => {
          console.log('SMTP config saved:', config);
          // TODO: Save SMTP configuration to backend
        }}
      />
      <DomainVerificationModal
        isOpen={showDomainModal}
        onClose={() => setShowDomainModal(false)}
      />
    </div>
  );
}

export default ColdOutreachPage;
